import React, { useState, useEffect, useCallback, useRef } from 'react';
import { proposalsService, templatesService, digitalContractsService, contractTemplatesService, clientsService, contractsService, pricingService, Quote } from '../services/api';
import { fmtCNPJ, maskCNPJ, fmtDate, fmtDateTime, getOffsetDateStr, parseLocalDate } from '../utils/formatters';
import CollapsibleSection from './CollapsibleSection';
import { investimentoFinal, resumoDaEmpresa, resumoDasCondicoes, resumoDasObservacoes, resumoDosItens } from '../utils/resumoDaProposta';
import { cobraAcimaDoContratado, linhaInclusa, moverItem, quantidadeFaturada, subtotalDaLinha } from '../utils/propostaItens';
import { ehLinhaDerivada, sincronizarInclusos } from '../utils/inclusosDoPacote';
import EditorDeSignatarios from './EditorDeSignatarios';
import { SignerForm, IPCOM_SIGNERS, errosDosSignatarios, payloadDeSigners } from '../utils/signatarios';
import { COMPANY } from '../config/company';
import ProposalGenerationModal from './ProposalGenerationModal';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm, useConfirmarPerigo } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { relatoDoLembrete, relatoDaFalhaDoLembrete } from '../utils/lembrete';
import Icon from './shared/Icon';
import MaisAcoes, { ItemDeMenu } from './shared/MaisAcoes';
import CopiarLinkDeAssinatura from './shared/CopiarLinkDeAssinatura';
import { SEM_BARRA, TONE_BADGE, TONE_BARRA, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { mensagemDoErroDeCarga, useEstadoDaCarga, EstadoDaLista, textos, Carga, lerSecao, acaoPrincipal, acaoSecundaria, campo } from '../design';
import { lerContratoDigitalAssinado, recusaSemContratoDigital } from '../utils/contratoDigitalDaProposta';
import { transicoesDaProposta, ROTULO_DA_TRANSICAO, confirmacaoDeTransicao, motivoDoServidor, Transicao, fraseDaPropostaTravada, FRASE_DOS_DADOS_DO_LEAD, CANAIS_DO_ACEITE, porqueNaoAprovar, estadoVisivelDaProposta, acoesDaProposta, avisoDaPropostaVencida, alertaDeValidade, PROPOSTA_ENCERRADA, marcarComoEnviada } from '../utils/crm';
import { horaDeBrasilia } from '../utils/email';
import { lerValorBR, formatarValorBR } from '../utils/valorBR';
import { aplicarClausulaPS, fraseDaClausulaPS, moedaDoContrato, VARIAVEL_DA_CLAUSULA_PS } from '../utils/clausulaDoContrato';
import { linkPublicoDaProposta, templatePadraoDaProposta } from '../utils/linkDaProposta';

/**
 * O resultado de anexar um PDF à assinatura recém-criada (08/10/2026). Os endpoints de sincronização respondem a
 * falha NO CORPO (`{ success: false, error }`, HTTP 200), não por exceção: um `try/catch` sozinho não a enxerga.
 * "Já está nos anexos" é o caso esperado quando o PDF entrou junto da criação, e conta como anexado.
 */
type ResultadoDoAnexo = { anexado: boolean; texto: string };
async function anexarPdf(sincronizar: () => Promise<any>): Promise<ResultadoDoAnexo> {
  try {
    const r = await sincronizar();
    if (r?.success) return { anexado: true, texto: 'anexado' };
    const erro = String(r?.error || 'o servidor não confirmou');
    if (/já está nos anexos/i.test(erro)) return { anexado: true, texto: 'anexado' };
    return { anexado: false, texto: `NÃO anexado (${erro})` };
  } catch (e) {
    return { anexado: false, texto: `NÃO anexado (${mensagemDoErroDeCarga(e)})` };
  }
}

// Signatários fixos da IPCOM (atalho de preenchimento no modal de assinatura)
const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


// Monta o endereço completo do contratante. Prioriza o campo legado `address`;
// se vazio, compõe a partir dos campos estruturados (street/number/... ) que o
// formulário de lead novo preenche. Sem isso o contrato saía "[ENDEREÇO NÃO INFORMADO]".
const buildEnderecoCompleto = (lead: any): string => {
  if (lead?.address && String(lead.address).trim()) return String(lead.address).trim();
  if (!lead) return '';
  const clean = (s: any) => String(s ?? '').trim().replace(/[;,\s]+$/, '');
  const street = clean(lead.street);
  const number = clean(lead.number);
  const complement = clean(lead.complement);
  const neighborhood = clean(lead.neighborhood);
  const city = clean(lead.city);
  const state = clean(lead.state);
  const zip = clean(lead.zipCode);

  const logradouro = [street, number, complement].filter(Boolean).join(', ');
  const cidadeUf = [city, state].filter(Boolean).join('/');
  let out = [logradouro, neighborhood, cidadeUf].filter(Boolean).join(' - ');
  if (zip) out += (out ? ' - ' : '') + `CEP ${zip}`;
  return out;
};

// Prazo de retenção das gravações puxado da PROPOSTA: produto CLOUD-GRAV-NNA
// (05A=5 anos, 10A=10 anos) sobrepõe o padrão de 1 ano da cláusula 7.3.
const NUM_EXTENSO: Record<number, string> = { 1: 'um', 2: 'dois', 3: 'três', 4: 'quatro', 5: 'cinco', 6: 'seis', 7: 'sete', 8: 'oito', 9: 'nove', 10: 'dez' };
const retencaoGravacaoTexto = (proposal: any): string => {
  let items: any[] = [];
  const raw = proposal?.items;
  if (Array.isArray(raw)) items = raw;
  else if (typeof raw === 'string') { try { items = JSON.parse(raw); } catch { items = []; } }
  let anos = 1; // padrão da cláusula
  for (const it of items) {
    const m = String(it?.productCode || '').match(/GRAV-0*(\d+)A/i);
    if (m) { anos = Math.max(anos, parseInt(m[1], 10)); }
  }
  const ext = NUM_EXTENSO[anos] || String(anos);
  return `${anos} (${ext}) ano${anos > 1 ? 's' : ''}`;
};

const replaceContractVariables = (template: string, proposal: any, lead: any, proposalTemplateId?: number | null) => {
  const today = new Date();
  const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  const replacements: Record<string, string> = {
    '{{ oportunidade.empresa.razaoSocial }}': proposal?.clientName || lead?.companyName || '',
    '{{ oportunidade.empresa.nomeFantasia }}': proposal?.clientTradeName || lead?.tradeName || '',
    '{{ oportunidade.empresa.cnpj }}': fmtCNPJ(proposal?.clientCnpj || lead?.cnpj || ''),
    '{{ oportunidade.empresa.enderecoCompleto }}': buildEnderecoCompleto(lead) || '[ENDEREÇO NÃO INFORMADO]',
    '{{ oportunidade.empresa.email }}': proposal?.clientEmail || lead?.email || '',
    '{{ oportunidade.empresa.telefone }}': proposal?.clientPhone || lead?.phone || '',
    '{{ oportunidade.contato.nome }}': proposal?.contactName || lead?.contactName || '',
    '{{ oportunidade.propostaAprovada.sigla }}': proposal?.code || '',
    // O MESMO link do botão Link do cartão, com template e v= (pedido do Heitor, 28/10/2026: o do contrato saía sem o v=).
    '{{ oportunidade.propostaAprovada.urlLinkPublico }}': linkPublicoDaProposta(proposal, proposalTemplateId),
    '{{ oportunidade.propostaMaisNova.valor|moeda(2, oportunidade.propostaMaisNova.moeda) }}': moedaDoContrato(proposal?.totalPS),
    '{{ oportunidade.propostaMaisNova.valorMrr|moeda(2, oportunidade.propostaMaisNova.moeda) }}': moedaDoContrato(proposal?.totalMRR),
    '{{ oportunidade.proposta.parcelas }}': String(proposal?.installments || 10),
    [VARIAVEL_DA_CLAUSULA_PS]: fraseDaClausulaPS(proposal),
    '{{ oportunidade.proposta.formaPagamento }}': proposal?.paymentMethod || 'Boleto',
    '{{ oportunidade.proposta.vigenciaMeses }}': String(proposal?.contractMonths || 12),
    '{{ oportunidade.retencaoGravacao }}': retencaoGravacaoTexto(proposal),
    '{{ generico.hojeExtenso }}': `Cascavel, ${today.getDate()} de ${meses[today.getMonth()]} de ${today.getFullYear()}`,
    '{{ generico.dataHoje }}': today.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
    '{{ contratada.razaoSocial }}': COMPANY.name,
    '{{ contratada.cnpj }}': COMPANY.cnpj,
    '{{ contratada.endereco }}': COMPANY.address,
  };

  let result = aplicarClausulaPS(template, proposal);
  for (const [key, value] of Object.entries(replacements)) {
    result = result.split(key).join(value);
  }
  return result;
};

interface ProposalItem {
  id: number; productId: number | null; productCode: string; name: string;
  description: string; type: string; quantity: number; unitPrice: number;
  discount: number; subtotal: number;
  /** Id do produto-pacote que gerou esta linha. Ausente = linha do vendedor. */
  includedByProductId?: number | null;
  /**
   * Quantidade que o Guia de Preços cobrou, presente só quando MAIOR que a
   * contratada (regra monotônica de faixas, backend #231). Ausente em preço
   * negociado, que é por unidade contratada.
   */
  billedQuantity?: number;
}

interface ProposalsTabProps {
  onLeadUpdate?: () => void; leadId: number;
  /**
   * A FICHA EM QUATRO ABAS (16/10/2026): a mesma aba de dados serve Propostas e Contrato (uma carga, os mesmos modais),
   * mostrando uma seção de cada vez. O vínculo "→ Contrato #57" leva à aba Contrato; o "da PRO…" do contrato volta.
   */
  secao?: 'propostas' | 'contrato';
  onVerProposta?: () => void;
  onVerContrato?: () => void;
  /** As listas lidas, para os rótulos das abas da ficha ("Propostas 1", "Contrato 2/4 assinaturas"). */
  onResumo?: (propostas: any[], contratos: any[]) => void; leadData: any; products: any[]; canEdit: boolean;
  /**
   * A carga de onde `products` veio (a principal do App). Sem ela, catálogo vazio parece catálogo sem produto: o
   * editor oferecia uma busca que não achava nada, e o item salvo aparecia sem nome de produto.
   */
  cargaDosProdutos?: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm'>;
}

// Cor (12/10/2026, PR-2 de cor do CRM): o selo de status sai do mapa de tons. "Expirada" era o laranja do acento.
const statusColors: any = {
  'Rascunho': TONE_BADGE.neutral, 'Enviada': TONE_BADGE.progress,
  'Visualizada': TONE_BADGE.info, 'Aceita': TONE_BADGE.success,
  'Recusada': TONE_BADGE.danger, 'Expirada': TONE_BADGE.warning,
  'Cancelada': TONE_BADGE.neutral,
};

const contractStatusColors: any = {
  'Rascunho': TONE_BADGE.neutral, 'Aguardando Assinatura': TONE_BADGE.warning,
  'Assinado': TONE_BADGE.success, 'Cancelado': TONE_BADGE.danger,
};

/**
 * C4 (12/10/2026, bloco C): o unitário era `type="number"`, que mostrava "39.9" e nunca "39,90". Campo de texto que lê
 * o que a pessoa digita ("39,9", "1.234,5", "39.9") e, ao sair, formata com duas casas. No escopo do módulo, e não
 * dentro do componente: declarado lá dentro, o React remontaria o <input> a cada render e o campo perderia o foco.
 */
function CampoDeUnitario({ valor, onValor, disabled, title, className }: { valor: number; onValor: (n: number) => void; disabled?: boolean; title?: string; className: string }) {
  const [texto, setTexto] = React.useState(formatarValorBR(valor));
  const [editando, setEditando] = React.useState(false);
  React.useEffect(() => { if (!editando) setTexto(formatarValorBR(valor)); }, [valor, editando]);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={texto}
      onFocus={() => setEditando(true)}
      onChange={(e) => { setTexto(e.target.value); const n = lerValorBR(e.target.value); if (n !== null) onValor(n); }}
      onBlur={() => { setEditando(false); const n = lerValorBR(texto); onValor(n ?? 0); setTexto(formatarValorBR(n ?? 0)); }}
      disabled={disabled}
      title={title}
      aria-label="Valor unitário"
      className={className}
    />
  );
}

const ProposalsTab: React.FC<ProposalsTabProps> = ({ leadId, leadData, products, canEdit, onLeadUpdate, cargaDosProdutos, secao = 'propostas', onVerProposta, onVerContrato, onResumo }) => {
  const [mostrarEncerradas, setMostrarEncerradas] = useState(false);
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  const avisar = useAviso();
  // Menu overflow "⋯" por card (proposta/contrato) — ações secundárias.
  // Os handlers do cartão recebem o evento só para o stopPropagation; o item do MaisAcoes não passa evento.
  const SEM_EVENTO = { stopPropagation: () => {}, preventDefault: () => {} } as any;
  // Modal de detalhes do aceite/recusa da proposta (dados que o cliente preencheu).
  const [acceptModalProposal, setAcceptModalProposal] = useState<any>(null);
  const [proposals, setProposals] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [digitalContracts, setDigitalContracts] = useState<any[]>([]);
  /**
   * ⛔ A carga da aba (08/10/2026, Fase 2 tela 2). O catch só fazia console.error: com a API fora, a aba dizia
   * "Propostas (0)", "Nenhuma proposta criada", os KPIs em R$ 0,00 e 0%, e oferecia "Nova Proposta", "Criar primeira
   * proposta" e "Importar HTML". E o "Carregando..." trocava a aba INTEIRA a cada recarga (perdendo a rolagem).
   */
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState<any>(null);

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedProposalForLink, setSelectedProposalForLink] = useState<any>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [waBtnCopied, setWaBtnCopied] = useState(false);
  // L1/M1 (Heitor, 28/10/2026): em Rascunho, o Copiar do Link marca como enviada (padrão) ou só copia para conferir. A
  // escolha visível no modal é a confirmação; depois, a faixa diz o que aconteceu (ou por que não marcou).
  const [aoCopiar, setAoCopiar] = useState<'marcar' | 'conferir'>('marcar');
  const [marcacao, setMarcacao] = useState<{ ok: boolean; hora?: string; motivo?: string } | null>(null);

  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedProposalForContract, setSelectedProposalForContract] = useState<any>(null);
  const [contractTemplates, setContractTemplates] = useState<any[]>([]);
  // Os templates de contrato são SEÇÃO à parte da carga: sem eles a aba segue, mas o modal de gerar contrato diz que
  // a lista não carregou em vez de "Nenhum template cadastrado".
  const [erroDosModelos, setErroDosModelos] = useState<string | null>(null);
  const [selectedContractTemplateId, setSelectedContractTemplateId] = useState<number | null>(null);
  const [contractPreview, setContractPreview] = useState('');
  const [generatingContract, setGeneratingContract] = useState(false);

  // Estado para modal de assinatura
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [selectedContractForSignature, setSelectedContractForSignature] = useState<any>(null);
  // Quando setado, o modal de assinatura envia o PDF TIMBRADO da proposta (não o contrato digital)
  const [signatureProposalTarget, setSignatureProposalTarget] = useState<any>(null);
  const [signers, setSigners] = useState<SignerForm[]>([]);
  const [sendingForSignature, setSendingForSignature] = useState(false);

  // Estado para modal de status dos signatários
  const [signersStatusModalOpen, setSignersStatusModalOpen] = useState(false);
  const [signersStatusData, setSignersStatusData] = useState<any>(null);
  const [loadingSignersStatus, setLoadingSignersStatus] = useState(false);

  // Estado para modal enviar proposta por email
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [selectedProposalForEmail, setSelectedProposalForEmail] = useState<any>(null);
  // Modo do modal de envio: 'proposal' (proposta por e-mail) ou 'contract-review'
  // (minuta do contrato pra validação) — reusa o MESMO seletor de destinatários.
  const [emailModalMode, setEmailModalMode] = useState<'proposal' | 'contract-review'>('proposal');
  const [selectedContractForReview, setSelectedContractForReview] = useState<any>(null);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [sendingProposalEmail, setSendingProposalEmail] = useState(false);
  const [sendEmailResult, setSendEmailResult] = useState<{ sentTo: string[]; errors: string[] } | null>(null);

  // Estado para modal criar assinatura de proposta aceita
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [selectedProposalForSubscription, setSelectedProposalForSubscription] = useState<any>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [creatingSubscription, setCreatingSubscription] = useState(false);

  const [clientName, setClientName] = useState('');
  const [clientTradeName, setClientTradeName] = useState('');
  const [clientCnpj, setClientCnpj] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [installments, setInstallments] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('Boleto');
  const [contractMonths, setContractMonths] = useState(12);
  const [validUntil, setValidUntil] = useState('');
  /**
   * Qual documento o cliente vê no link público. Não é deduzido dos itens: uma
   * proposta pode ter WhatsApp e voz ao mesmo tempo (PRO20260085 tem os dois),
   * e nenhuma regra automática acerta isso — quem sabe o que está vendendo é o
   * vendedor. `null` no banco vale como 'voz'.
   */
  const [templateVariant, setTemplateVariant] = useState<'voz' | 'whatsapp'>('voz');
  /**
   * Índice da linha sendo arrastada. A ordem do array é a ordem impressa no
   * documento, então reordenar é editar conteúdo — até aqui só dava para
   * corrigir por SQL (a PRO20260086 foi reordenada no banco três vezes num dia).
   */
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [proposalItems, setProposalItems] = useState<ProposalItem[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState<string | null>(null); // item id or 'new'

  const loadData = useCallback(async () => {
    await rodarCarga(async () => {
      const [p, t, dc, cl] = await Promise.all([
        proposalsService.getByLead(leadId),
        templatesService.getActive(),
        digitalContractsService.getByLead(leadId),
        clientsService.getAll()
      ]);
      setProposals(Array.isArray(p) ? p : []);
      setTemplates(Array.isArray(t) ? t : []);
      setDigitalContracts(Array.isArray(dc) ? dc : []);
      setClients(Array.isArray(cl) ? cl : []);

      const defaultTemplate = (Array.isArray(t) ? t : []).find((tpl: any) => tpl.isDefault);
      if (defaultTemplate) setSelectedTemplateId(defaultTemplate.id);

      const modelos = await lerSecao(contractTemplatesService.getAll());
      setErroDosModelos(modelos.erro);
      if (!modelos.erro) {
        const ctTemplates = Array.isArray(modelos.valor) ? modelos.valor : [];
        setContractTemplates(ctTemplates);
        const defaultCT = ctTemplates.find((ct: any) => ct.isDefault);
        if (defaultCT) setSelectedContractTemplateId(defaultCT.id);
      }
    });
  }, [leadId, rodarCarga]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (selectedContractTemplateId && selectedProposalForContract) {
      const template = contractTemplates.find(t => t.id === selectedContractTemplateId);
      if (template) {
        // Template PADRÃO da PROPOSTA (mesmo que o link compartilhado usa) → o
        // link no contrato abre com o layout que o cliente viu.
        const preview = replaceContractVariables(template.content || '', selectedProposalForContract, leadData, templatePadraoDaProposta(templates));
        setContractPreview(preview);
      }
    }
  }, [selectedContractTemplateId, selectedProposalForContract, contractTemplates, leadData, templates]);

  const calculateTotals = (items: ProposalItem[], discount: number) => {
    let totalPS = 0, totalMRR = 0;
    items.forEach(item => {
      if (item.type === 'Serviço' || item.type === 'PS') totalPS += item.subtotal;
      else totalMRR += item.subtotal;
    });
    const subtotal = totalPS + totalMRR;
    const discountValue = subtotal * (discount / 100);
    return { totalPS, totalMRR, discountValue, finalTotal: subtotal - discountValue };
  };

  const totals = calculateTotals(proposalItems, discountPercent);
  // Uma definição só para o bloco Investimento e para o resumo do cabeçalho:
  // dois números que discordassem sobre o mesmo carrinho seriam pior que um.
  const investimento = investimentoFinal(totals.totalMRR, totals.totalPS, discountPercent);

  /**
   * As linhas do que JÁ VEM no pacote nascem do catálogo, não da memória de quem
   * monta. Rodar a cada mudança do carrinho só é seguro porque
   * `sincronizarInclusos` devolve a MESMA lista quando não há o que fazer: é
   * isso que faz o efeito parar em vez de se realimentar a cada render.
   */
  useEffect(() => {
    if (!modalOpen) return;
    setProposalItems(prev => sincronizarInclusos(prev, products));
  }, [modalOpen, proposalItems, products]);

  const resetForm = () => {
    setClientName(''); setClientTradeName(''); setClientCnpj(''); setClientEmail(''); setClientPhone('');
    setContactName(''); setContactEmail(''); setContactPhone(''); setNotes(''); setInternalNotes('');
    setDiscountPercent(0); setInstallments(1); setPaymentMethod('Boleto'); setContractMonths(12);
    setValidUntil(''); setProposalItems([]); setTemplateVariant('voz');
  };

  const openNewProposal = () => {
    resetForm();
    setClientName(leadData?.companyName || ''); setClientTradeName(leadData?.tradeName || '');
    setClientCnpj(leadData?.cnpj || ''); setClientEmail(leadData?.email || '');
    setClientPhone(leadData?.phone || ''); setContactName(leadData?.contactName || '');
    setContactEmail(leadData?.email || ''); setContactPhone(leadData?.phone || '');
    setValidUntil(getOffsetDateStr(30));
    setEditingProposal(null); setModalOpen(true);
  };

  const parseItemsFromDB = (rawItems: any): ProposalItem[] => {
    if (!rawItems) return [];
    let parsed = rawItems;
    if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch { return []; } }
    if (!Array.isArray(parsed)) return [];
    return parsed.flat(10).filter((i: any) => i && typeof i === 'object' && (i.name || i.productId))
      .map((i: any, idx: number) => ({
        id: i.id || Date.now() + idx, productId: i.productId || null, productCode: i.productCode || '',
        name: i.name || '', description: i.description || '', type: i.type || 'MRR',
        // Sem carregar de volta a marca de origem, a linha derivada voltaria do
        // banco como linha comum e a sincronização criaria uma SEGUNDA ao lado.
        includedByProductId: Number(i.includedByProductId) > 0 ? Number(i.includedByProductId) : undefined,
        // Sem isto, reabrir a proposta mostraria quantidade × unitário e
        // divergiria do subtotal gravado, que veio da cotação.
        billedQuantity: Number(i.billedQuantity) > (Number(i.quantity) || 0) ? Number(i.billedQuantity) : undefined,
        // Aceita os DOIS formatos: unitPrice/subtotal (form) e unitValue/totalValue
        // (itens de contrato/importação) — itens importados apareciam zerados
        quantity: Number(i.quantity) || 1, unitPrice: Number(i.unitPrice ?? i.unitValue) || 0,
        discount: Number(i.discount) || 0, subtotal: Number(i.subtotal ?? i.totalValue) || 0,
      }));
  };

  const openEditProposal = (proposal: any) => {
    setProposalItems(parseItemsFromDB(proposal.items));
    setClientName(proposal.clientName || ''); setClientTradeName(proposal.clientTradeName || '');
    setClientCnpj(proposal.clientCnpj || ''); setClientEmail(proposal.clientEmail || '');
    setClientPhone(proposal.clientPhone || ''); setContactName(proposal.contactName || '');
    setContactEmail(proposal.contactEmail || ''); setContactPhone(proposal.contactPhone || '');
    setNotes(proposal.notes || ''); setInternalNotes(proposal.internalNotes || '');
    setDiscountPercent(Number(proposal.discountPercent) || 0); setInstallments(proposal.installments || 1);
    setPaymentMethod(proposal.paymentMethod || 'Boleto'); setContractMonths(proposal.contractMonths || 12);
    setValidUntil(proposal.validUntil ? proposal.validUntil.split('T')[0] : '');
    setTemplateVariant(proposal.templateVariant === 'whatsapp' ? 'whatsapp' : 'voz');
    setEditingProposal(proposal); setModalOpen(true);
  };

  /**
   * Solta a linha arrastada na posição da linha de destino. A conta mora em
   * `moverItem`, com teste: mover para baixo é onde o índice anda sozinho
   * depois da remoção, e errar isso embaralha a composição sem dar erro.
   */
  const soltarEm = (destino: number) => {
    setProposalItems(prev => (arrastando == null ? prev : moverItem(prev, arrastando, destino)));
    setArrastando(null);
  };

  /** Mesma reordenação pelo teclado, com a alça em foco. Sem isso a única forma
   *  de reordenar seria o mouse — e o vendedor que monta proposta no notebook
   *  sem mouse ficaria de volta no SQL. */
  const moverPorTeclado = (idx: number, delta: number) => {
    setProposalItems(prev => moverItem(prev, idx, idx + delta));
  };

  /**
   * ⛔ PREÇO DA FAIXA NÃO CONFIRMADO (08/10/2026). Produto com faixa de volume tem o unitário dado pela COTAÇÃO do
   * Guia de Preços; enquanto ela não volta, a linha mostra o valor de REFERÊNCIA do produto. A cotação que falhava
   * caía num `catch` que "mantinha o valor de referência", e a proposta podia ser salva com o unitário-base no lugar
   * do da faixa, sem aviso. Agora a linha diz que o preço não foi confirmado, oferece Recotar, e Salvar fica travado
   * até a cotação voltar. Preço DIGITADO à mão é preço negociado (sai do Guia de propósito) e limpa a marca.
   * Valor: o id do item → o motivo, ou COTANDO enquanto a resposta não chega.
   */
  const COTANDO = 'cotando';
  const [faixaPendente, setFaixaPendente] = useState<Record<number, string>>({});
  // Só a cotação mais recente de cada item vale: digitar 1 e depois 10 dispara duas, e a do 1 chegando por último
  // deixaria na linha o preço de outra quantidade.
  const cotacaoDoItem = useRef<Record<number, number>>({});
  const marcarFaixa = (itemId: number, motivo: string | null) => setFaixaPendente((prev) => {
    const n = { ...prev };
    if (motivo === null) delete n[itemId]; else n[itemId] = motivo;
    return n;
  });
  const esquecerCotacao = (itemId: number) => {
    cotacaoDoItem.current[itemId] = (cotacaoDoItem.current[itemId] || 0) + 1;
    marcarFaixa(itemId, null);
  };
  useEffect(() => { if (!modalOpen) { setFaixaPendente({}); cotacaoDoItem.current = {}; } }, [modalOpen]);

  const removeItem = (itemId: number) => {
    esquecerCotacao(itemId);
    setProposalItems(prev => prev.filter(i => i.id !== itemId));
  };

  // Produto com faixa de volume: o preço unitário vem do Guia de Preços (backend)
  // e muda com a quantidade. Sem faixa, fica o valor de referência do produto.
  const applyGuidePrice = async (itemId: number, productId: number, quantity: number) => {
    const prod = products.find((p: any) => p.id === productId);
    const minha = (cotacaoDoItem.current[itemId] || 0) + 1;
    cotacaoDoItem.current[itemId] = minha;
    if (!prod || !Array.isArray(prod.priceTiers) || prod.priceTiers.length === 0) { marcarFaixa(itemId, null); return; }
    marcarFaixa(itemId, COTANDO);
    try {
      const q = await pricingService.quote([{ productId, quantity: Math.max(1, Math.floor(Number(quantity) || 1)) }]);
      if (cotacaoDoItem.current[itemId] !== minha) return;
      const line = q?.lines?.[0];
      if (!line) { marcarFaixa(itemId, 'a cotação não trouxe a linha do produto'); return; }
      // O subtotal sai da COTAÇÃO, não de quantidade × unitário. Desde a regra
      // monotônica de faixas (backend #231) quem tem 190 é cobrado como 191,
      // e recompor aqui mostraria R$ 30,00 a menos do que a fatura emite.
      setProposalItems(prev => prev.map(it => {
        if (it.id !== itemId) return it;
        const faturada = Number(line.billedQuantity) > (Number(it.quantity) || 0)
          ? Number(line.billedQuantity)
          : undefined;
        const desconto = Number(it.discount) || 0;
        return {
          ...it,
          unitPrice: line.unitPrice,
          billedQuantity: faturada,
          subtotal: line.subtotal * (1 - desconto / 100),
        };
      }));
      marcarFaixa(itemId, null);
    } catch (e) {
      if (cotacaoDoItem.current[itemId] !== minha) return;
      marcarFaixa(itemId, mensagemDoErroDeCarga(e));
    }
  };

  const updateItem = (itemId: number, field: string, value: any) => {
    const current = proposalItems.find(i => i.id === itemId);
    if (field === 'productId' && value) applyGuidePrice(itemId, Number(value), Number(current?.quantity) || 1);
    if (field === 'quantity' && current?.productId) applyGuidePrice(itemId, Number(current.productId), Number(value) || 1);
    setProposalItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      const updated = { ...item, [field]: value };
      if (field === 'productId' && value) {
        const prod = products.find(p => p.id === Number(value));
        if (prod) {
          updated.productCode = prod.code || ''; updated.name = prod.name || '';
          updated.description = prod.description || ''; updated.type = prod.type || 'MRR';
          updated.unitPrice = Number(prod.value) || 0; updated.quantity = updated.quantity || 1;
          updated.discount = updated.discount || 0;
          // Produto trocado: a cotação anterior não vale mais. O applyGuidePrice
          // acima repõe o certo quando o produto novo tem faixa.
          updated.billedQuantity = undefined;
          updated.subtotal = updated.unitPrice * updated.quantity * (1 - updated.discount / 100);
        }
      }
      if (['quantity', 'unitPrice', 'discount'].includes(field)) {
        // ⛔ Digitar o unitário é PREÇO NEGOCIADO, e negociado é por unidade
        // CONTRATADA. Quem concedeu desconto não quis cobrar o mínimo de uma
        // faixa que o cliente não alcançou — então sair do Guia de Preços
        // limpa a quantidade faturada.
        if (field === 'unitPrice') updated.billedQuantity = undefined;
        // e não espera mais cotação nenhuma: a que estiver em voo não sobrescreve o negociado (ver faixaPendente).
        if (field === 'unitPrice') esquecerCotacao(itemId);
        // Mudar a quantidade dispara nova cotação (applyGuidePrice, acima) e ela
        // é quem dá a palavra final; aqui fica um valor provisório para a linha
        // não piscar um número velho enquanto a resposta não chega.
        if (field === 'quantity') updated.billedQuantity = undefined;
        updated.subtotal = subtotalDaLinha(updated);
      }
      return updated;
    }));
  };

  const faixaNaoConfirmada = Object.values(faixaPendente);
  const porqueSalvarTravado = faixaNaoConfirmada.length === 0 ? undefined
    : faixaNaoConfirmada.every((m) => m === COTANDO)
      ? 'Aguardando a cotação do preço da faixa.'
      : 'Há item com o preço da faixa não confirmado: recote (ou digite o preço negociado) antes de salvar.';

  const handleSave = async () => {
    // A trava do botão é a primeira linha; esta é a segunda, para qualquer caminho que chame o salvar.
    if (porqueSalvarTravado) { avisar(`Não salvei: ${porqueSalvarTravado}`, 'warning'); return; }
    // C6 (12/10/2026): editar proposta vencida salvava sem renovar; agora exige a validade nova.
    if (avisoDaPropostaVencida(editingProposal, validUntil)) { avisar('Não salvei: a proposta venceu. Defina a nova validade (a partir de hoje) em Condições.', 'warning'); return; }
    const cleanItems = proposalItems.filter(item => item && (item.name || item.productId))
      .map(item => ({ id: item.id, productId: item.productId, productCode: item.productCode || '',
        name: item.name || '', description: item.description || '', type: item.type || 'MRR',
        quantity: Number(item.quantity) || 1, unitPrice: Number(item.unitPrice) || 0,
        discount: Number(item.discount) || 0, subtotal: Number(item.subtotal) || 0,
        // `items` é jsonb e o DTO do backend aceita chave extra; este map é que
        // descartaria o flag, e sem ele a linha derivada vira órfã na volta.
        ...(Number(item.includedByProductId) > 0 ? { includedByProductId: Number(item.includedByProductId) } : {}),
        ...(Number(item.billedQuantity) > (Number(item.quantity) || 0) ? { billedQuantity: Number(item.billedQuantity) } : {}) }));
    const currentTotals = calculateTotals(proposalItems, discountPercent);
    const payload = { leadId, clientName, clientTradeName, clientCnpj, clientEmail, clientPhone,
      contactName, contactEmail, contactPhone, items: cleanItems, totalPS: currentTotals.totalPS,
      totalMRR: currentTotals.totalMRR, discountPercent, discountValue: currentTotals.discountValue,
      finalTotal: currentTotals.finalTotal, installments, paymentMethod, contractMonths, validUntil,
      // Sempre explícito, nunca omitido: o UPDATE do backend grava a variante com
      // COALESCE, então PUT sem o campo PRESERVA o valor anterior. Mandar sempre
      // é o que faz "voltar para Telefonia" funcionar — omitir manteria WhatsApp.
      templateVariant,
      notes, internalNotes, status: editingProposal?.status || 'Rascunho' };
    setSaving(true);
    try {
      if (editingProposal) await proposalsService.update(editingProposal.id, payload);
      else await proposalsService.create(payload);
      setModalOpen(false); if (onLeadUpdate) onLeadUpdate(); loadData();
    } catch (e) { avisar(`Não salvei a proposta: ${motivoDoServidor(e)}`, 'error'); }
    setSaving(false);
  };

  // EXCLUIR PROPOSTA
  const handleDeleteProposal = async (proposal: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!await confirmarPerigo(`Tem certeza que deseja excluir a proposta ${proposal.code}?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      await proposalsService.delete(proposal.id);
      loadData();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      console.error('Erro ao excluir proposta:', error);
      avisar('Erro ao excluir proposta', 'error');
    }
  };

  // ALTERAR STATUS DA PROPOSTA. P1 (11/10/2026): era um <select> cru com qualquer transição (inclusive Aceita →
  // Rascunho) e um "Alterar status?" que não dizia o que acontecia. Agora só as transições de onde a proposta está, cada
  // uma com a consequência. PATCH :id/status: o update() completo NÃO persiste a coluna status.
  // E7 (12/10/2026): o aceite pelo operador exige quem aceitou e por qual canal; vai para acceptedBy* no backend.
  const [aceiteManual, setAceiteManual] = useState<{ proposal: any; nome: string; canal: string; salvando: boolean } | null>(null);
  const registrarAceite = async () => {
    if (!aceiteManual) return;
    const { proposal, nome, canal } = aceiteManual;
    if (porqueNaoAprovar(nome, canal)) return;
    setAceiteManual({ ...aceiteManual, salvando: true });
    try {
      await proposalsService.updateStatus(proposal.id, 'Aceita', { acceptedByName: nome.trim(), acceptanceChannel: canal });
      setAceiteManual(null);
      loadData();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      avisar(`Não registrei o aceite de ${proposal.code}: ${motivoDoServidor(error)}`, 'error');
      setAceiteManual((a) => (a ? { ...a, salvando: false } : a));
    }
  };

  const mudarStatus = async (proposal: any, para: Transicao) => {
    if (para === 'Aceita') { setAceiteManual({ proposal, nome: '', canal: '', salvando: false }); return; }
    if (!(await confirmar(confirmacaoDeTransicao(proposal, para)))) return;
    try {
      await proposalsService.updateStatus(proposal.id, para);
      loadData();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      avisar(`Não mudei o status de ${proposal.code}: ${motivoDoServidor(error)}`, 'error');
    }
  };

  // Marcar como enviada pelo ⋯ (pedido do Heitor, 28/10/2026): o único botão estava em Gerar com IA › Envio.
  const marcarComoEnviadaPeloMenu = async (proposal: any) => {
    if (!(await confirmar({
      title: `Marcar a ${proposal.code} como enviada?`,
      message: `${proposal.code}: de "Rascunho" para "Enviada".`,
      consequence: 'Use quando o link foi mandado por WhatsApp ou em mão. O cliente passa a ver Aceitar e Recusar no link, e a abertura dele fica registrada.',
      confirmLabel: 'Marcar como enviada',
    }))) return;
    try {
      await proposalsService.updateStatus(proposal.id, 'Enviada');
      loadData();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      avisar(`Não marquei ${proposal.code} como enviada: ${motivoDoServidor(error)}`, 'error');
    }
  };

  // EXCLUIR CONTRATO
  const handleDeleteContract = async (contract: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await confirmar({ title: `Excluir o contrato #${contract.id}?`, consequence: 'Esta ação não pode ser desfeita.', danger: true, confirmLabel: 'Excluir' }))) return;
    try {
      await digitalContractsService.delete(contract.id);
      loadData();
    } catch (error: any) {
      // 404 = o contrato já não existe. O usuário pediu que sumisse e ele sumiu;
      // alertar aqui seria trocar um bug silencioso por um alarme falso (dois
      // admins apagando o mesmo item, ou lista desatualizada). Só recarrega.
      if (error?.response?.status === 404) { loadData(); return; }
      console.error('Erro ao excluir contrato:', error);
      avisar('Erro ao excluir contrato', 'error');
    }
  };

  // PDF EXTERNO: anexa um PDF pronto (ex.: proposta desenhada por IA) — vira o
  // documento exibido no link público; e-mail e fluxo de aceite não mudam
  const customPdfInputRef = useRef<HTMLInputElement | null>(null);
  const [customPdfTarget, setCustomPdfTarget] = useState<any>(null);
  // Geração pela IA (fase 4): modal com prontidão, job, versões e gate
  const [generationTarget, setGenerationTarget] = useState<any>(null);

  const handleCustomPdfClick = async (p: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (p.customPdfS3Key || p.customHtmlS3Key) {
      if (await confirmar({ title: 'Substituir o documento externo?', message: `A proposta ${p.code} já tem documento externo.`, consequence: 'Escolher outro arquivo substitui o que o link público mostra. "Não" leva à opção de remover.', confirmLabel: 'Escolher outro arquivo', cancelLabel: 'Não' })) {
        setCustomPdfTarget(p);
        customPdfInputRef.current?.click();
      } else if (await confirmarPerigo('Remover o PDF externo? O link público volta ao layout padrão da proposta.')) {
        proposalsService.removeCustomPdf(p.id).then(() => loadData()).catch((err) => avisar(`Não removi o documento externo: ${motivoDoServidor(err)}`, 'error'));
      }
      return;
    }
    setCustomPdfTarget(p);
    customPdfInputRef.current?.click();
  };

  const handleCustomPdfSelected = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file || !customPdfTarget) return;
    const okType = file.type === 'application/pdf' || file.type === 'text/html' || /\.html?$/i.test(file.name);
    if (!okType) { avisar('Apenas PDF ou HTML são aceitos', 'warning'); return; }
    try {
      await proposalsService.uploadCustomPdf(customPdfTarget.id, file);
      avisar(`✅ PDF externo anexado à proposta ${customPdfTarget.code}.\n\nO link público agora exibe este documento (com o aceite normal). O e-mail continua o mesmo — ele envia o link.`, 'success');
      await loadData();
    } catch (err: any) {
      avisar(err.response?.data?.message || 'Erro ao anexar PDF', 'error');
    }
    setCustomPdfTarget(null);
    if (customPdfInputRef.current) customPdfInputRef.current.value = '';
  };

  // IMPORTAR HTML/PDF: cria a proposta do lead com os PRODUTOS HUB (preço por
  // faixa aplicado automático) e anexa o documento externo num passo só
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importForm, setImportForm] = useState<{ mrr: string; ps: string; validUntil: string; file: File | null }>({ mrr: '', ps: '', validUntil: '', file: null });
  const [importItems, setImportItems] = useState<Array<{ productId: number | ''; qty: string }>>([{ productId: '', qty: '1' }]);
  const [importSaving, setImportSaving] = useState(false);
  const [importAnalyzing, setImportAnalyzing] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);

  // Guia de Preços: faixa, piso e memória de cálculo vêm do backend
  // (POST /pricing/quote). O front não tem tabela própria: mostra o que o motor devolve.
  const [importQuote, setImportQuote] = useState<Quote | null>(null);
  // ⛔ A cotação do Importar é a FONTE do preço da faixa e do PISO do Guia (08/10/2026). Ela falhando caía em
  // `setImportQuote(null)`, e então os itens saíam com o preço de referência do produto e a conferência do piso
  // simplesmente não acontecia. Agora: com itens, "Criar proposta" só com a cotação desta lista confirmada.
  const [importCotacao, setImportCotacao] = useState<{ estado: 'vazio' | 'cotando' | 'ok' | 'falhou'; motivo?: string }>({ estado: 'vazio' });
  useEffect(() => {
    const lines = importItems
      .filter(r => r.productId && parseFloat(r.qty) > 0)
      .map(r => ({ productId: Number(r.productId), quantity: Math.floor(parseFloat(r.qty)) }));
    if (!lines.length) { setImportQuote(null); setImportCotacao({ estado: 'vazio' }); return; }
    let cancelled = false;
    setImportCotacao({ estado: 'cotando' });
    const t = setTimeout(async () => {
      try {
        const q = await pricingService.quote(lines);
        if (!cancelled) { setImportQuote(q); setImportCotacao({ estado: 'ok' }); }
      } catch (e) {
        if (!cancelled) { setImportQuote(null); setImportCotacao({ estado: 'falhou', motivo: mensagemDoErroDeCarga(e) }); }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [importItems]);
  const porqueImportarTravado = importCotacao.estado === 'cotando'
    ? 'Aguardando a cotação do Guia de Preços.'
    : importCotacao.estado === 'falhou'
      ? `O preço da faixa e o piso do Guia não foram confirmados (${importCotacao.motivo}). Os itens sairiam com o preço de referência do produto.`
      : undefined;
  const quoteLineFor = (productId: number) => importQuote?.lines.find(l => l.productId === productId);

  const hubProducts = products.filter((p: any) => p.status && String(p.code || '').startsWith('HUB-'));
  const buildImportItems = () => importItems
    .filter(r => r.productId && parseFloat(r.qty) > 0)
    .map(r => {
      const prod = products.find((p: any) => p.id === Number(r.productId));
      const qty = parseFloat(r.qty) || 1;
      const unit = (prod && quoteLineFor(prod.id)?.unitPrice) ?? (Number(prod?.value) || 0);
      const total = Math.round(qty * unit * 100) / 100;
      return {
        name: prod?.name || 'Item',
        type: prod?.type === 'Serviço' || prod?.type === 'PS' ? 'PS' : 'MRR',
        quantity: qty,
        unitValue: unit,
        totalValue: total,
        unitPrice: unit,
        subtotal: total,
        discount: 0,
        productId: prod?.id,
        productCode: prod?.code,
        description: prod?.description || undefined,
      };
    });
  const importTotals = (() => {
    const its = buildImportItems();
    const mrr = its.filter(i => i.type === 'MRR').reduce((s, i) => s + i.totalValue, 0);
    const ps = its.filter(i => i.type === 'PS').reduce((s, i) => s + i.totalValue, 0);
    return { mrr: Math.round(mrr * 100) / 100, ps: Math.round(ps * 100) / 100, hasItems: its.length > 0 };
  })();

  // Ao escolher um HTML, a IA extrai os itens HUB do documento e pré-preenche as linhas
  const handleImportFileSelected = async (f: File) => {
    setImportForm(fm => ({ ...fm, file: f }));
    const isHtml = /\.html?$/i.test(f.name) || f.type === 'text/html';
    if (!isHtml) return;
    setImportAnalyzing(true);
    try {
      const res = await proposalsService.analyzeDocument(f);
      const rows = (res?.items || [])
        .map((it: any) => {
          const p = products.find((pp: any) => pp.code === it.productCode);
          return p ? { productId: p.id as number, qty: String(it.quantity || 1) } : null;
        })
        .filter(Boolean) as Array<{ productId: number; qty: string }>;
      if (rows.length) setImportItems(rows);
      if (res?.validUntil) setImportForm(fm => ({ ...fm, validUntil: res.validUntil }));
    } catch (e) {
      console.error('Análise IA do documento falhou — preencha os itens manualmente:', e);
    } finally {
      setImportAnalyzing(false);
    }
  };

  const handleImportSubmit = async () => {
    if (!importForm.file) { avisar('Selecione o arquivo HTML ou PDF da proposta', 'warning'); return; }
    const its = buildImportItems();
    // Com itens, os totais saem dos produtos; sem itens, valem os campos manuais
    const mrr = its.length > 0 ? importTotals.mrr : (parseFloat(String(importForm.mrr).replace(',', '.')) || 0);
    const ps = its.length > 0 ? importTotals.ps : (parseFloat(String(importForm.ps).replace(',', '.')) || 0);
    if (mrr <= 0 && ps <= 0) { avisar('Adicione itens ou informe o MRR/P&S da proposta', 'warning'); return; }
    if (its.length > 0 && porqueImportarTravado) { avisar(`Não criei a proposta: ${porqueImportarTravado}`, 'warning'); return; }
    if (importQuote && importQuote.floorAdjustment > 0 && !(await confirmar({ title: 'Criar abaixo do piso?', message: `As posições somam R$ ${fmt(importQuote.positionsSubtotal)}, abaixo do piso de R$ ${fmt(importQuote.floor)}/mês do Guia de Preços.`, confirmLabel: 'Criar mesmo assim' }))) return;
    setImportSaving(true);
    try {
      const created = await proposalsService.create({
        leadId,
        clientName: leadData?.companyName || leadData?.tradeName || 'Cliente',
        clientTradeName: leadData?.tradeName || '',
        clientCnpj: leadData?.cnpj || '',
        clientEmail: leadData?.email || '',
        clientPhone: leadData?.phone || '',
        contactName: leadData?.contactName || '',
        contactEmail: leadData?.email || '',
        contactPhone: leadData?.phone || '',
        validUntil: importForm.validUntil || getOffsetDateStr(30),
        totalMRR: mrr,
        totalPS: ps,
        finalTotal: mrr + ps,
        items: its,
      });
      await proposalsService.uploadCustomPdf(created.id, importForm.file);
      avisar(`✅ Proposta ${created.code} criada com ${its.length} item(ns) e o documento anexado.\n\nDica: use o código ${created.code} ao gerar o documento na IA pros números baterem.`, 'success');
      setImportModalOpen(false);
      setImportItems([{ productId: '', qty: '1' }]);
      await loadData();
    } catch (err: any) {
      avisar(err.response?.data?.message || 'Erro ao importar proposta', 'error');
    }
    setImportSaving(false);
  };

  const openLinkModal = (proposal: any, e: React.MouseEvent) => {
    e.stopPropagation(); setSelectedProposalForLink(proposal); setLinkCopied(false); setAoCopiar('marcar'); setMarcacao(null);
    setSelectedTemplateId(templatePadraoDaProposta(templates)); setLinkModalOpen(true);
  };

  // A mesma função do contrato digital (utils/linkDaProposta): os dois links não têm como divergir.
  const getPublicLink = () => linkPublicoDaProposta(selectedProposalForLink, selectedTemplateId);

  const rotuloDoCopiar = selectedProposalForLink && aoCopiar === 'marcar' && marcarComoEnviada(selectedProposalForLink).pode ? 'Copiar e marcar' : 'Copiar';

  // O link já foi copiado quando isto roda: se a marcação falhar, o operador tem o link E sabe que segue em Rascunho.
  const marcarSeEscolheu = async () => {
    const p = selectedProposalForLink;
    if (!p || aoCopiar !== 'marcar' || !marcarComoEnviada(p).pode) return;
    try {
      const atualizada = await proposalsService.updateStatus(p.id, 'Enviada');
      setSelectedProposalForLink({ ...p, ...(atualizada || {}), status: 'Enviada' });
      setMarcacao({ ok: true, hora: horaDeBrasilia(atualizada?.sentAt || new Date().toISOString()) });
      loadData();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      setMarcacao({ ok: false, motivo: motivoDoServidor(error) });
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(getPublicLink()); }
    catch { const input = document.createElement('input'); input.value = getPublicLink();
      document.body.appendChild(input); input.select(); document.execCommand('copy');
      document.body.removeChild(input); }
    setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000);
    await marcarSeEscolheu();
  };

  // Valor para colar na VARIÁVEL do botão de URL do template do WhatsApp (sistema de
  // atendimento). O template lá tem a base fixa https://hub.ipcom.app.br/p/{{1}} — então
  // o {{1}} recebe só o que vem DEPOIS de /p/ (code + querystring), nunca a URL inteira.
  const getWhatsAppButtonValue = () => {
    const full = getPublicLink();
    const marker = '/p/';
    const i = full.indexOf(marker);
    return i >= 0 ? full.slice(i + marker.length) : full;
  };

  const copyWhatsAppButton = async () => {
    const val = getWhatsAppButtonValue();
    try { await navigator.clipboard.writeText(val); }
    catch { const input = document.createElement('input'); input.value = val;
      document.body.appendChild(input); input.select(); document.execCommand('copy');
      document.body.removeChild(input); }
    setWaBtnCopied(true); setTimeout(() => setWaBtnCopied(false), 2000);
    await marcarSeEscolheu();
  };

  // Destinatários sugeridos a partir do lead/entidade (mesma lógica pra proposta e contrato).
  const suggestRecipients = (entity: any): string[] => {
    const emails: string[] = [];
    if (entity?.clientEmail) emails.push(entity.clientEmail);
    if (entity?.contactEmail && entity.contactEmail !== entity.clientEmail) emails.push(entity.contactEmail);
    if (emails.length === 0 && leadData?.email) emails.push(leadData.email);
    if (leadData?.contactEmail && !emails.includes(leadData.contactEmail)) emails.push(leadData.contactEmail);
    return Array.from(new Set(emails.filter(Boolean)));
  };

  const openSendEmailModal = (proposal: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEmailModalMode('proposal');
    setSelectedContractForReview(null);
    setSelectedProposalForEmail(proposal);
    setSendEmailResult(null);
    setEmailRecipients(suggestRecipients(proposal));
    setNewEmailInput('');
    setSendEmailModalOpen(true);
  };

  // Abre o MESMO modal de destinatários, mas em modo "validação do contrato".
  const openReviewEmailModal = (contract: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEmailModalMode('contract-review');
    setSelectedProposalForEmail(null);
    setSelectedContractForReview(contract);
    setSendEmailResult(null);
    setEmailRecipients(suggestRecipients(contract));
    setNewEmailInput('');
    setSendEmailModalOpen(true);
  };

  const handleAddEmailRecipient = () => {
    const email = newEmailInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { avisar('Email inválido', 'warning'); return; }
    if (emailRecipients.includes(email)) { avisar('Email já adicionado', 'warning'); return; }
    setEmailRecipients([...emailRecipients, email]);
    setNewEmailInput('');
  };

  const handleRemoveEmailRecipient = (email: string) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };

  const handleSendProposalEmail = async () => {
    if (emailRecipients.length === 0) return;
    setSendingProposalEmail(true);
    setSendEmailResult(null);
    try {
      if (emailModalMode === 'contract-review' && selectedContractForReview) {
        const result = await digitalContractsService.sendForReview(selectedContractForReview.id, emailRecipients);
        setSendEmailResult({ sentTo: result.sentTo || [], errors: result.errors || [] });
        if (result.sentTo?.length > 0 && onLeadUpdate) onLeadUpdate();
      } else if (selectedProposalForEmail) {
        const result = await proposalsService.sendEmail(selectedProposalForEmail.id, emailRecipients);
        setSendEmailResult({ sentTo: result.sentTo || [], errors: result.errors || [] });
        if (result.sentTo?.length > 0) await loadData();
      }
    } catch (error: any) {
      console.error('Erro ao enviar email:', error);
      setSendEmailResult({ sentTo: [], errors: [error.response?.data?.message || 'Erro ao enviar email'] });
    }
    setSendingProposalEmail(false);
  };

  const openContractModal = (proposal: any, e: React.MouseEvent) => {
    e.stopPropagation(); setSelectedProposalForContract(proposal);
    const defaultCT = contractTemplates.find((t: any) => t.isDefault);
    setSelectedContractTemplateId(defaultCT?.id || contractTemplates[0]?.id || null);
    setContractModalOpen(true);
  };

  const handleGenerateContract = async () => {
    if (!selectedContractTemplateId) { avisar('Selecione um template de contrato', 'warning'); return; }
    setGeneratingContract(true);
    try {
      await digitalContractsService.create({
        proposalId: selectedProposalForContract.id, proposalCode: selectedProposalForContract.code,
        leadId, templateId: selectedContractTemplateId, content: contractPreview, status: 'Rascunho',
        clientName: selectedProposalForContract.clientName, clientEmail: selectedProposalForContract.clientEmail,
        clientCnpj: selectedProposalForContract.clientCnpj, clientAddress: buildEnderecoCompleto(leadData) || '',
        totalPS: selectedProposalForContract.totalPS, totalMRR: selectedProposalForContract.totalMRR,
      });
      await loadData();
      avisar('Contrato gerado com sucesso!\n\nPróximos passos:\n- Revisar o contrato\n- Enviar para assinatura digital', 'success');
      setContractModalOpen(false);
    } catch (error) { avisar(`Não gerei o contrato: ${motivoDoServidor(error)}`, 'error'); }
    setGeneratingContract(false);
  };

  // Abrir modal de assinatura
  const openSignatureModal = (contract: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSignatureProposalTarget(null);
    setSelectedContractForSignature(contract);
    // Inicializar com o signatário principal (cliente)
    setSigners([{
      name: contract.clientName || '',
      email: contract.clientEmail || '',
      cpf: '',
      birthday: '',
      phone: '',
      role: 'sign',
      authType: 'email',
      _expanded: true,
    }]);
    setSignatureModalOpen(true);
  };

  // Assinar o PDF timbrado da proposta (ClickSign) — mesmo padrão dos ofícios.
  // Assinatura interna: começa com o preset do Fábio (Certificado Digital).
  const openProposalSignatureModal = (p: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedContractForSignature(null);
    setSignatureProposalTarget(p);
    setSigners([{ ...IPCOM_SIGNERS.fabio, _expanded: false }]);
    setSignatureModalOpen(true);
  };

  const closeSignatureModal = () => {
    setSignatureModalOpen(false);
    setSignatureProposalTarget(null);
  };

  // Enviar para assinatura via ClickSign
  const handleSendForSignature = async () => {
    const errors = errosDosSignatarios(signers);
    if (errors.length > 0) {
      avisar('Corrija os seguintes erros:\n\n' + errors.join('\n'), 'warning');
      return;
    }
    setSendingForSignature(true);
    try {
      const payloadSigners = payloadDeSigners(signers);
      const result = signatureProposalTarget
        ? await proposalsService.sendForSignature(signatureProposalTarget.id, {
            signers: payloadSigners,
            templateId: selectedTemplateId || undefined,
          })
        : await digitalContractsService.sendForSignature(selectedContractForSignature.id, { signers: payloadSigners });
      if (result.success) {
        const count = result.signers?.length || signers.length;
        const docLabel = signatureProposalTarget ? `PDF da proposta ${signatureProposalTarget.code} enviado` : 'Contrato enviado';
        let msg = `${docLabel} para ${count} signatário(s)!\n\nCada signatário receberá um email com o link para assinar.`;
        if (result.errors?.length > 0) {
          msg += `\n\n⚠️ ${result.errors.length} signatário(s) falharam:\n` +
            result.errors.map((e: any) => `• ${e.name} (${e.email}): ${e.error}`).join('\n');
        }
        avisar(msg, result.errors?.length > 0 ? 'warning' : 'success');
        closeSignatureModal();
        loadData();
      } else {
        avisar('Erro ao enviar para assinatura', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao enviar para assinatura:', error);
      const serverMsg = error.response?.data?.message || error.message;
      avisar('Erro ao enviar para assinatura:\n\n' + serverMsg, 'error');
    }
    setSendingForSignature(false);
  };

  // Reenviar notificação de assinatura
  const handleResendNotification = async (contract: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await confirmar({ title: 'Reenviar o lembrete de assinatura?', message: `Contrato #${contract.id}.`, consequence: 'Sai um e-mail para os signatários que ainda não assinaram.', confirmLabel: 'Reenviar' }))) return;
    try {
      // success:false dizia só "Erro ao enviar lembrete": o motivo (o lembrete ia para quem já tinha assinado, lead 86,
      // contrato #55) ficava no log do servidor. O relato diz o motivo e, com o contrato novo, quem recebeu.
      const { texto, tom } = relatoDoLembrete(await digitalContractsService.resendSignatureNotification(contract.id));
      avisar(texto, tom);
    } catch (error: any) {
      const { texto, tom } = relatoDaFalhaDoLembrete(error);
      avisar(texto, tom);
    }
  };

  // Cancelar assinatura
  // Abrir o PDF do contrato (endpoint autenticado → blob). A aba é aberta no
  // clique (gesto do usuário) p/ não cair no bloqueador de popup; navega após o fetch.
  const handleViewContractPdf = async (contractId: number) => {
    const win = window.open('', '_blank');
    try {
      const blob = await digitalContractsService.getPdf(contractId);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      if (win) win.close();
      console.error('Erro ao abrir contrato:', error);
      avisar('Não foi possível abrir o contrato: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  // Abrir o documento assinado (backend devolve a URL da ClickSign; abrimos direto).
  const handleViewSignedDoc = async (contractId: number) => {
    const win = window.open('', '_blank');
    try {
      const { url } = await digitalContractsService.getSignedUrl(contractId);
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch (error: any) {
      if (win) win.close();
      console.error('Erro ao abrir documento assinado:', error);
      avisar('Não foi possível abrir o documento assinado: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleCancelSignature = async (contract: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!await confirmarPerigo('Cancelar a assinatura deste contrato?\n\nO link de assinatura será invalidado.')) return;
    try {
      const result = await digitalContractsService.cancelSignature(contract.id);
      if (result.success) {
        avisar('Assinatura cancelada', 'success');
        loadData();
      } else {
        avisar('Erro ao cancelar assinatura', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao cancelar assinatura:', error);
      avisar('Erro ao cancelar: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  // === Assinatura do PDF timbrado da proposta (ClickSign) ===
  const handleOpenSignedProposalPdf = async (p: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const win = window.open('', '_blank');
    try {
      const blob = await proposalsService.getSignedPdf(p.id);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      if (win) win.close();
      avisar('Não foi possível abrir o PDF assinado: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  // Consulta o ClickSign ao vivo (também é o fallback caso o webhook não chegue:
  // se todos assinaram, o backend baixa o assinado e fecha o ciclo aqui).
  const handleCheckProposalSignature = async (p: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const st = await proposalsService.getSignatureStatus(p.id);
      const lines = (st.signers || []).map((s: any) =>
        `• ${s.name}: ${s.status === 'signed' ? `✓ assinado${s.signedAt ? ' em ' + fmtDateTime(s.signedAt) : ''}` : '⏳ pendente'}`);
      avisar(`Assinatura do PDF ${p.code}: ${st.signatureStatus === 'assinado' ? 'ASSINADO ✓' : 'aguardando'}\n\n${lines.join('\n')}`, 'info');
      loadData();
    } catch (error: any) {
      avisar('Erro ao verificar assinatura: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleResendProposalSignature = async (p: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await confirmar({ title: 'Reenviar o lembrete de assinatura?', message: `PDF da proposta ${p.code}.`, consequence: 'Sai um e-mail do ClickSign para os signatários que ainda não assinaram.', confirmLabel: 'Reenviar' }))) return;
    try {
      const { texto, tom } = relatoDoLembrete(await proposalsService.resendSignatureNotification(p.id));
      avisar(texto, tom);
    } catch (error: any) {
      const { texto, tom } = relatoDaFalhaDoLembrete(error);
      avisar(texto, tom);
    }
  };

  const handleCancelProposalSignature = async (p: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!await confirmarPerigo('Cancelar a assinatura do PDF desta proposta?\n\nO link de assinatura no ClickSign será invalidado.')) return;
    try {
      await proposalsService.cancelSignature(p.id);
      avisar('Assinatura cancelada', 'success');
      loadData();
    } catch (error: any) {
      avisar('Erro ao cancelar: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  // Ver status dos signatários
  const handleViewSignersStatus = async (contract: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSignersStatusModalOpen(true);
    // Abre INSTANTÂNEO a partir do snapshot persistido (contract.signers) — funciona
    // pra qualquer status (inclusive Assinado) e sem depender da ClickSign. Depois
    // refresca ao vivo em background (atualiza/persiste). (30/06/2026)
    const persisted: any[] = Array.isArray(contract.signers) ? contract.signers : [];
    if (persisted.length > 0) {
      const signedCount = persisted.filter((s) => s.status === 'signed').length;
      setSignersStatusData({
        contractId: contract.id,
        proposalCode: contract.proposalCode,
        signers: persisted,
        signedCount,
        totalSigners: persisted.length,
        pendingCount: persisted.length - signedCount,
        completionPercentage: persisted.length ? Math.round((signedCount / persisted.length) * 100) : 0,
      });
    } else {
      setSignersStatusData(null);
      setLoadingSignersStatus(true);
    }
    try {
      const data = await digitalContractsService.getSignersStatus(contract.id);
      if (data && Array.isArray(data.signers) && data.signers.length > 0) {
        setSignersStatusData({ ...data, contractId: contract.id, proposalCode: contract.proposalCode });
      } else if (persisted.length === 0) {
        setSignersStatusData(data || { error: 'Nenhum signatário encontrado' });
      }
    } catch (error: any) {
      console.error('Erro ao carregar status dos signatários:', error);
      if (persisted.length === 0) setSignersStatusData({ error: error.response?.data?.message || error.message });
    }
    setLoadingSignersStatus(false);
  };

  // Calcular métricas das propostas. MRR/P&S POTENCIAL só somam propostas VIVAS
  // (fix 07/07: Cancelada/Recusada/Expirada não contam — antes somava as 4 e o
  // card mostrava R$5.561 pra 1 rascunho de R$816 + 3 canceladas).
  const DEAD_PROPOSAL = PROPOSTA_ENCERRADA;
  const liveProposals = proposals.filter((p: any) => !DEAD_PROPOSAL.includes(p.status));
  // As encerradas (canceladas, recusadas, expiradas) ficam recolhidas numa linha, depois das vivas: na ficha da BIS2BIS a
  // proposta aceita dividia a tela com duas canceladas no mesmo peso.
  const deadProposals = proposals.filter((p: any) => DEAD_PROPOSAL.includes(p.status));
  const fraseDasEncerradas = (() => {
    const n = (st: string, um: string, varios: string) => { const k = deadProposals.filter((p: any) => p.status === st).length; return k ? `${k} ${k === 1 ? um : varios}` : null; };
    return [n('Cancelada', 'cancelada', 'canceladas'), n('Recusada', 'recusada', 'recusadas'), n('Expirada', 'expirada', 'expiradas')].filter(Boolean).join(' · ');
  })();
  const proposalStats = {
    total: proposals.length,
    totalMRR: liveProposals.reduce((acc: number, p: any) => acc + (Number(p.totalMRR) || 0), 0),
    totalPS: liveProposals.reduce((acc: number, p: any) => acc + (Number(p.totalPS) || 0), 0),
    aceitas: proposals.filter((p: any) => p.status === 'Aceita').length,
    pendentes: proposals.filter((p: any) => ['Rascunho', 'Enviada', 'Visualizada'].includes(p.status)).length,
    recusadas: proposals.filter((p: any) => p.status === 'Recusada').length,
    expiradas: proposals.filter((p: any) => p.status === 'Expirada').length,
  };

  // Duplicar proposta
  const handleDuplicateProposal = async (proposal: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await confirmar({ title: 'Duplicar a proposta?', message: `${proposal.code} vira uma nova proposta em Rascunho, com os mesmos itens.`, confirmLabel: 'Duplicar' }))) return;

    // Preparar items para duplicação
    const items = parseItemsFromDB(proposal.items);
    const cleanItems = items.map(item => ({
      ...item,
      id: Date.now() + Math.random()
    }));

    const payload = {
      leadId,
      clientName: proposal.clientName,
      clientTradeName: proposal.clientTradeName,
      clientCnpj: proposal.clientCnpj,
      clientEmail: proposal.clientEmail,
      clientPhone: proposal.clientPhone,
      contactName: proposal.contactName,
      contactEmail: proposal.contactEmail,
      contactPhone: proposal.contactPhone,
      items: cleanItems,
      totalPS: proposal.totalPS,
      totalMRR: proposal.totalMRR,
      discountPercent: proposal.discountPercent || 0,
      discountValue: proposal.discountValue || 0,
      finalTotal: proposal.finalTotal,
      installments: proposal.installments || 1,
      paymentMethod: proposal.paymentMethod || 'Boleto',
      contractMonths: proposal.contractMonths || 12,
      validUntil: getOffsetDateStr(30),
      notes: proposal.notes || '',
      internalNotes: `Duplicada de ${proposal.code}`,
      status: 'Rascunho'
    };

    try {
      await proposalsService.create(payload);
      loadData();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      console.error('Erro ao duplicar proposta:', error);
      avisar('Erro ao duplicar proposta', 'error');
    }
  };

  // Abrir modal para criar assinatura
  const openSubscriptionModal = (proposal: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProposalForSubscription(proposal);
    setSelectedClientId(null);
    setClientSearch('');
    setSubscriptionModalOpen(true);
  };

  // Filtrar clientes baseado na busca
  const filteredClients = clients.filter((c: any) => {
    if (!clientSearch) return true;
    const search = clientSearch.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(search) ||
      (c.companyName || '').toLowerCase().includes(search) ||
      (c.tradeName || '').toLowerCase().includes(search) ||
      (c.cnpj || '').includes(search)
    );
  }).sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

  // Criar assinatura a partir da proposta
  const handleCreateSubscription = async () => {
    if (!selectedClientId) {
      avisar('Selecione um cliente', 'warning');
      return;
    }
    if (!selectedProposalForSubscription) return;

    setCreatingSubscription(true);
    try {
      const proposal = selectedProposalForSubscription;
      const client = clients.find((c: any) => c.id === selectedClientId);

      // Parse items da proposta
      let rawItems = proposal.items;
      if (typeof rawItems === 'string') {
        try { rawItems = JSON.parse(rawItems); } catch { rawItems = []; }
      }
      const items = (Array.isArray(rawItems) ? rawItems.flat(10) : [])
        .filter((i: any) => i && typeof i === 'object' && (i.name || i.productId));

      // Mapear itens para formato do contrato
      const contractItems = items.map((item: any) => ({
        productId: item.productId || null,
        productCode: item.productCode || '',
        name: item.name || '',
        description: item.description || '',
        type: (item.type === 'Serviço' || item.type === 'PS') ? 'PS' : 'MRR',
        quantity: Number(item.quantity) || 1,
        unitValue: Number(item.unitPrice) || 0,
        discount: Number(item.discount) || 0,
        totalValue: Number(item.subtotal) || 0,
      }));

      // Buscar contrato digital assinado desta proposta.
      // ⛔ FALHA FECHADA (08/10/2026): a leitura que falhava virava "não há contrato", e a assinatura era GRAVADA como
      // Rascunho e sem o PDF assinado, com a tela dizendo "sucesso". Sem a leitura, não cria nada
      // (utils/contratoDigitalDaProposta, a mesma peça do "Sim, criar assinatura" do App).
      const leitura = await lerContratoDigitalAssinado(proposal);
      if ('motivo' in leitura) {
        avisar(recusaSemContratoDigital(proposal.code, leitura.motivo), 'error');
        setCreatingSubscription(false);
        return;
      }
      const attachments: any[] = leitura.anexo ? [leitura.anexo] : [];

      // Calcular MRR e PS total
      const mrr = contractItems
        .filter((i: any) => i.type === 'MRR')
        .reduce((sum: number, i: any) => sum + (i.totalValue || 0), 0);
      const totalPS = contractItems
        .filter((i: any) => i.type === 'PS')
        .reduce((sum: number, i: any) => sum + (i.totalValue || 0), 0);

      // Criar contrato/assinatura
      const contractPayload = {
        clientId: selectedClientId,
        proposalId: proposal.id,
        items: contractItems,
        mrr,
        totalPS,
        activationTotal: totalPS,
        activationInstallments: proposal.installments || 1,
        contractMonths: proposal.contractMonths || 12,
        status: 'Ativo',
        signatureStatus: attachments.length > 0 ? 'Assinado' : 'Rascunho',
        notes: `Gerado a partir da proposta ${proposal.code}`,
        attachments,
      };

      const newContract = await contractsService.create(contractPayload);
      const contractId = newContract?.id || newContract?.data?.id;

      // Anexa o PDF da proposta e o do contrato assinado, e DIZ o que anexou e o que não (08/10/2026). Antes, as duas
      // falhas caíam em catch vazio e a mensagem afirmava "O contrato digital assinado foi anexado automaticamente"
      // mesmo quando não tinha sido.
      const linhas: string[] = [];
      let faltou = false;
      if (contractId) {
        const daProposta = await anexarPdf(() => contractsService.syncProposalPdf(contractId));
        linhas.push(`• PDF da proposta: ${daProposta.texto}`);
        faltou = faltou || !daProposta.anexado;
        // Chamado sempre, como antes: o backend baixa o PDF pelo ClickSign mesmo quando o contrato digital não trouxe
        // signedPdfUrl. "Não encontrado ou não assinado" é a resposta de quando não há assinatura, e não é falha.
        const assinado = await anexarPdf(() => contractsService.syncSignedPdf(contractId));
        if (!assinado.anexado && /não encontrado ou não assinado/i.test(assinado.texto)) {
          linhas.push('• Contrato digital assinado: não há contrato assinado desta proposta (a assinatura fica como Rascunho)');
        } else {
          linhas.push(`• Contrato digital assinado: ${assinado.texto}`);
          faltou = faltou || !assinado.anexado;
        }
      } else {
        linhas.push('• Não consegui ler o número da assinatura criada: nenhum PDF foi anexado.');
        faltou = true;
      }
      if (faltou) linhas.push('', 'Anexe o que faltou pela tela de Assinaturas.');

      avisar(`Assinatura criada para ${client?.name || 'o cliente'}.\n\n${linhas.join('\n')}`, 'success');
      setSubscriptionModalOpen(false);
      if (onLeadUpdate) onLeadUpdate();
    } catch (error: any) {
      console.error('Erro ao criar assinatura:', error);
      avisar('Erro ao criar assinatura: ' + (error.response?.data?.message || error.message), 'error');
    }
    setCreatingSubscription(false);
  };

  // Número sem fonte é "—" (nenhuma carga deu certo ainda); a carga é tudo-ou-nada, sem parcial.
  const temFonte = carga.atualizadoEm !== null;
  useEffect(() => {
    if (temFonte && onResumo) onResumo(proposals, digitalContracts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temFonte, proposals, digitalContracts]);
  const prontaParaCriar = carga.estado === 'pronto';
  const produtosNaoCarregaram = cargaDosProdutos?.estado === 'erro' && products.length === 0;
  const semFonte = textos.numeroSemFonte;

  return (
    <div className="space-y-6">
      {/* input oculto do PDF externo da proposta */}
      {generationTarget && (
        <ProposalGenerationModal proposal={generationTarget} leadId={leadId} leadData={leadData} canEdit={canEdit} onClose={() => { setGenerationTarget(null); loadData(); if (onLeadUpdate) onLeadUpdate(); }} onChanged={loadData}
          onSendEmail={(p) => { setGenerationTarget(null); openSendEmailModal(p, { stopPropagation: () => undefined } as any); }} />
      )}
      <input ref={customPdfInputRef} type="file" accept="application/pdf,text/html,.html,.htm" className="hidden"
        onChange={e => handleCustomPdfSelected(e.target.files)} />

      {/* Modal Importar HTML/PDF (proposta desenhada por IA) */}
      {importModalOpen && (
        // Arquivo e itens escolhidos: não fecha ao clicar fora (como já era). z-60: abre
        // por cima da ficha do lead. O roxo da primária e do realce vira o laranja.
        <CascaDeModal
          largura="xs"
          zIndex="z-[60]"
          titulo="📥 Importar proposta (HTML/PDF)"
          onClose={() => setImportModalOpen(false)}
          topo={<p className="px-5 pt-3 text-sm text-slate-400">O documento vira a página do link público; os valores alimentam o CRM e o e-mail.</p>}
          corpoClassName="p-5 space-y-4"
          rodape={
            <>
              <button onClick={() => setImportModalOpen(false)} className={`flex-1 px-4 py-2.5 rounded-lg ${acaoSecundaria}`}>Cancelar</button>
              <button onClick={handleImportSubmit} disabled={importSaving || importAnalyzing || (importTotals.hasItems && !!porqueImportarTravado)} title={importTotals.hasItems ? porqueImportarTravado : undefined} className={`flex-1 px-4 py-2.5 rounded-lg disabled:opacity-50 ${acaoPrincipal}`}>
                {importSaving ? 'Importando…' : importAnalyzing ? 'Analisando…' : 'Criar proposta'}
              </button>
            </>
          }
        >
              <div
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-orange-500'); }}
                onDragLeave={e => e.currentTarget.classList.remove('ring-2', 'ring-orange-500')}
                onDrop={e => {
                  e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-orange-500');
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleImportFileSelected(f);
                }}
                className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center"
              >
                <input id="import-html-file" type="file" accept="application/pdf,text/html,.html,.htm" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleImportFileSelected(f); }} />
                <label htmlFor="import-html-file" className="cursor-pointer text-sm text-slate-300">
                  {importForm.file ? <span className="text-slate-100 font-medium">📄 {importForm.file.name}</span> : <>Arraste o arquivo aqui ou <span className="text-orange-400 underline">clique para escolher</span> (HTML ou PDF)</>}
                </label>
              </div>
              {importAnalyzing && (
                <div className={`flex items-center gap-2 text-xs border rounded-lg px-3 py-2 ${TONE_SURFACE.info} ${TONE_BORDER.info} ${TONE_TEXT.info}`}>
                  <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                  Analisando o documento com IA — os itens serão preenchidos automaticamente…
                </div>
              )}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Itens (produtos HUB — preço da faixa aplicado automático)</label>
                {importCotacao.estado === 'falhou' && (
                  <div className={`mb-2 px-3 py-1.5 rounded border text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-cotacao-import='falhou'>
                    ⚠ {porqueImportarTravado} Mude uma quantidade para cotar de novo.
                  </div>
                )}
                <div className="space-y-2">
                  {importItems.map((row, idx) => {
                    const prod = products.find((p: any) => p.id === Number(row.productId));
                    const qty = parseFloat(row.qty) || 0;
                    const ql = prod ? quoteLineFor(prod.id) : undefined;
                    const unit = ql?.unitPrice ?? (prod ? Number(prod.value) || 0 : 0);
                    const faixa = ql?.tier ? `faixa ${ql.tier.label}: R$ ${fmt(unit)}` : '';
                    return (
                      <div key={idx} className="flex gap-2 items-start">
                        <div className="flex-1">
                          <select value={row.productId} onChange={e => setImportItems(arr => arr.map((r, i) => i === idx ? { ...r, productId: e.target.value ? Number(e.target.value) : '' } : r))}
                            className={`w-full px-2 py-2 ${campo} rounded text-sm`}>
                            <option value="">Selecione o produto…</option>
                            {hubProducts.map((p: any) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          {prod && qty > 0 && (
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              {faixa ? `${faixa} · ` : `R$ ${fmt(unit)} · `}total <span className="text-slate-200">R$ {fmt(qty * unit)}</span>{prod.type === 'Serviço' ? ' (único)' : '/mês'}
                            </div>
                          )}
                        </div>
                        <input type="number" min="1" value={row.qty} title="Quantidade"
                          onChange={e => setImportItems(arr => arr.map((r, i) => i === idx ? { ...r, qty: e.target.value } : r))}
                          className={`w-16 px-2 py-2 ${campo} rounded text-sm`} />
                        <button type="button" onClick={() => setImportItems(arr => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr)}
                          className="px-2 py-2 text-slate-500 hover:text-red-400" title="Remover">✕</button>
                      </div>
                    );
                  })}
                </div>
                <button type="button" onClick={() => setImportItems(arr => [...arr, { productId: '', qty: '1' }])}
                  className="mt-2 text-xs text-orange-400 hover:text-orange-300">+ Adicionar item</button>
                <div className="mt-2 p-2.5 bg-slate-700/40 rounded-lg border border-slate-600 text-sm text-slate-300 flex justify-between">
                  <span>MRR: <strong className={TONE_TEXT.open}>R$ {fmt(importTotals.mrr)}</strong>/mês</span>
                  <span>Setup: <strong className={TONE_TEXT.ps}>R$ {fmt(importTotals.ps)}</strong></span>
                </div>
                {importQuote && importQuote.floorAdjustment > 0 && (
                  <div className={`mt-1 text-xs ${TONE_TEXT.warning}`}>⚠ Posições somam R$ {fmt(importQuote.positionsSubtotal)}, abaixo do piso de R$ {fmt(importQuote.floor)}/mês do Guia de Preços</div>
                )}
                {importQuote && importQuote.enterprise && (
                  <div className={`mt-1 text-xs ${TONE_TEXT.warning}`}>⚠ Porte enterprise: acima de 300 posições a proposta sai de dimensionamento, com aprovador nomeado</div>
                )}
                {!importTotals.hasItems && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">MRR manual (sem itens)</label>
                      <input type="text" inputMode="decimal" placeholder="4519,00" className={`w-full px-3 py-2 ${campo} rounded text-sm`}
                        value={importForm.mrr} onChange={e => setImportForm(fm => ({ ...fm, mrr: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">P&S manual (sem itens)</label>
                      <input type="text" inputMode="decimal" placeholder="1500,00" className={`w-full px-3 py-2 ${campo} rounded text-sm`}
                        value={importForm.ps} onChange={e => setImportForm(fm => ({ ...fm, ps: e.target.value }))} />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Válida até</label>
                <input type="date" className={`w-full px-3 py-2 ${campo} rounded`}
                  value={importForm.validUntil} onChange={e => setImportForm(fm => ({ ...fm, validUntil: e.target.value }))} />
              </div>
        </CascaDeModal>
      )}
      {/* C1 (12/10/2026, bloco C): quatro cartões grandes para um lead ("Taxa de aceite 0%" de uma proposta só não diz
          nada) viram UMA linha, em peso, sem matiz. MRR e P&S somam só as propostas vivas, como antes. */}
      {secao === 'propostas' && <>
      <p className="text-sm text-slate-400" data-resumo-das-propostas>
        <span className="font-semibold text-white" data-kpi="total">{temFonte ? proposalStats.total : semFonte}</span>{' '}
        {temFonte && proposalStats.total === 1 ? 'proposta' : 'propostas'}
        {' · '}MRR <span className="font-semibold text-white" data-kpi="mrr">{temFonte ? `R$ ${fmt(proposalStats.totalMRR)}` : semFonte}</span>
        {' · '}P&S <span className="font-semibold text-white" data-kpi="ps">{temFonte ? `R$ ${fmt(proposalStats.totalPS)}` : semFonte}</span>
        {temFonte && proposalStats.total > 0 && proposalStats.aceitas + proposalStats.pendentes < proposalStats.total && (
          <span className="text-slate-500"> · somando só as vivas</span>
        )}
      </p>

      {/* PROPOSTAS */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">Propostas ({temFonte ? proposals.length : semFonte})</h3>
          {/* Criar e importar só com a carga PRONTA: com as propostas sem chegar, criar é criar sem ver a que já existe. */}
          {canEdit && prontaParaCriar && <button onClick={() => { setImportForm({ mrr: '', ps: '', validUntil: getOffsetDateStr(30), file: null }); setImportModalOpen(true); }} className="flex items-center gap-2 px-4 py-2 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg font-medium text-sm" title="Cria a proposta a partir de um HTML/PDF pronto (ex.: gerado por IA) — você só preenche MRR e P&S">📥 Importar HTML</button>}
          {canEdit && prontaParaCriar && <button onClick={openNewProposal} className="flex items-center gap-2 px-4 py-2 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg font-medium text-sm">+ Nova Proposta</button>}
        </div>
        <EstadoDaLista
          carga={carga}
          temDados={temFonte && proposals.length > 0}
          oQue="as propostas"
          onTentarDeNovo={loadData}
          vazio={(
            <div className="text-center text-slate-500 py-8 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-2xl mb-2">📋</p><p>Nenhuma proposta criada</p>
              {canEdit && <button onClick={openNewProposal} className="text-orange-500 hover:underline mt-2 text-sm">Criar primeira proposta</button>}
            </div>
          )}
        >
          <div className="space-y-3">
            {[...liveProposals, ...(mostrarEncerradas ? deadProposals : [])].map((p: any) => {
              const itemCount = parseItemsFromDB(p.items).length;
              // C2 (12/10/2026): o estado que manda é o vencido, com a fronteira da página pública (vale até o dia).
              const estado = estadoVisivelDaProposta(p);
              const acoes = acoesDaProposta(p);
              const isExpired = acoes.expirada;
              const isExpiringSoon = alertaDeValidade(p) === 'vence-em-breve';

              const linkedContract = digitalContracts.find((c: any) => c.proposalId === p.id || (p.code && c.proposalCode === p.code));
              // Produção (12/10/2026, visto pelo Heitor): a barra era VERMELHA numa proposta de selo "Expirada" em warning,
              // e laranja (o acento velho) em toda proposta viva. A barra é a exceção, no MESMO tom do selo: vencida ou
              // vencendo em 7 dias = warning; recusada = danger; o resto sem barra.
              const accent = (isExpired || isExpiringSoon) && p.status !== 'Aceita' && p.status !== 'Recusada' ? TONE_BARRA.warning
                : p.status === 'Recusada' ? TONE_BARRA.danger : SEM_BARRA;
              // O ⋯ do cartão (27/10/2026): era um menu próprio, azul-marinho, que abria para cima e sumia atrás da barra de
              // abas da ficha (cortado pelo overflow do modal). Agora é o MaisAcoes (portal, vira conforme o espaço), com os
              // itens neutros e só o Excluir em vermelho, separado por traço.
              const transicoes = canEdit && !isExpired ? transicoesDaProposta(p.status).filter((t) => t !== 'Cancelada' && t !== 'Rascunho') : [];
              const itensDoMenu: ItemDeMenu[] = [
                ...(canEdit && !isExpired && p.signatureStatus !== 'aguardando_assinatura' && p.signatureStatus !== 'assinado' ? [{ label: 'Assinar PDF (ClickSign)', onClick: () => openProposalSignatureModal(p, SEM_EVENTO) }] : []),
                ...(p.signatureStatus === 'aguardando_assinatura' ? [{ label: 'Verificar assinatura', onClick: () => handleCheckProposalSignature(p, SEM_EVENTO) }] : []),
                ...(p.signatureStatus === 'aguardando_assinatura' ? [{ label: 'Reenviar lembrete', onClick: () => handleResendProposalSignature(p, SEM_EVENTO) }] : []),
                ...(canEdit && p.signatureStatus === 'aguardando_assinatura' ? [{ label: 'Cancelar assinatura', onClick: () => handleCancelProposalSignature(p, SEM_EVENTO) }] : []),
                ...(canEdit && marcarComoEnviada(p).pode ? [{ label: '📤 Marcar como enviada', onClick: () => marcarComoEnviadaPeloMenu(p) }] : []),
                ...transicoes.map((t) => ({ label: ROTULO_DA_TRANSICAO[t], onClick: () => mudarStatus(p, t) })),
                ...(p.status === 'Aceita' ? [{ label: 'Criar assinatura', onClick: () => openSubscriptionModal(p, SEM_EVENTO) }] : []),
                ...(canEdit && transicoesDaProposta(p.status).includes('Cancelada') ? [{ label: ROTULO_DA_TRANSICAO.Cancelada, onClick: () => mudarStatus(p, 'Cancelada') }] : []),
                ...(canEdit && !isExpired && transicoesDaProposta(p.status).includes('Rascunho') ? [{ label: ROTULO_DA_TRANSICAO.Rascunho, onClick: () => mudarStatus(p, 'Rascunho') }] : []),
                ...(canEdit ? [{ label: 'Excluir', danger: true, separadoAntes: true, onClick: () => handleDeleteProposal(p, SEM_EVENTO) }] : []),
              ];
              return (
                <div key={p.id} data-cartao-da-proposta className={`bg-central-cartao rounded-lg border border-central-borda border-l-[3px] ${accent} p-3 hover:bg-central-elevado transition-colors cursor-pointer`} onClick={() => openEditProposal(p)}>
                  {/* Linha 1: tipo + código + status + valores */}
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span title="Proposta">📄</span>
                      <span className="font-mono text-xs text-slate-300 bg-slate-700 px-2 py-0.5 rounded">{p.code}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusColors[estado] || 'bg-slate-600'}`} data-estado-da-proposta>{estado}</span>
                      {itemCount > 0 && <span className="text-xs text-slate-500 whitespace-nowrap">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</span>}
                    </div>
                    <div className="text-right whitespace-nowrap text-xs">
                      <span className="text-slate-500">MRR</span> <span className="font-semibold text-green-400">R$ {fmt(p.totalMRR)}</span>
                      <span className="text-slate-600 mx-1">·</span>
                      <span className="text-slate-500">P&S</span> <span className={`font-semibold ${TONE_TEXT.ps}`}>R$ {fmt(p.totalPS)}</span>
                    </div>
                  </div>
                  {/* Linha 2: datas */}
                  <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <span>Criada {fmtDate(p.createdAt)}</span>
                    <span>•</span>
                    <span className={isExpired ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : ''}>
                      {isExpired ? `Venceu em ${fmtDate(p.validUntil)}` : isExpiringSoon ? '⏰ Expira em breve' : `Válida até ${fmtDate(p.validUntil)}`}
                    </span>
                    {p.contractMonths ? <><span>•</span><span>Vigência {p.contractMonths}m</span><span>•</span><span>MRR anual R$ {fmt((p.totalMRR || 0) * 12)}</span></> : null}
                  </div>
                  {/* Linha 3: aceite / visualização / recusa */}
                  {(p.status === 'Aceita' && (p.acceptedByName || p.acceptedAt)) && (
                    <div className="text-[11px] text-green-400 mt-1">✓ Aceita{p.acceptedByName ? ` por ${p.acceptedByName}` : ''}{p.acceptedAt ? ` · ${fmtDateTime(p.acceptedAt)}` : ''}</div>
                  )}
                  {(p.status === 'Recusada' && (p.rejectedByName || p.rejectionReason)) && (
                    <div className="text-[11px] text-red-400 mt-1">✕ Recusada{p.rejectedByName ? ` por ${p.rejectedByName}` : ''}{p.rejectionReason ? ` · ${p.rejectionReason}` : ''}</div>
                  )}
                  {(p.status !== 'Aceita' && p.status !== 'Recusada' && p.viewedAt) && (
                    <div className="text-[11px] text-blue-300 mt-1">👁 Visualizada em {fmtDateTime(p.viewedAt)}</div>
                  )}
                  {/* Assinatura do PDF timbrado (ClickSign) */}
                  {p.signatureStatus === 'aguardando_assinatura' && (
                    <div className="text-[11px] text-yellow-400 mt-1">🖊 PDF aguardando assinatura{Array.isArray(p.signatureSigners) && p.signatureSigners.length > 0 ? ` — ${p.signatureSigners.map((s: any) => s.name).join(', ')}` : ''}{p.sentForSignatureAt ? ` · enviado ${fmtDateTime(p.sentForSignatureAt)}` : ''}</div>
                  )}
                  {p.signatureStatus === 'assinado' && (
                    <div className="text-[11px] text-green-400 mt-1">🖊 PDF assinado{p.signedAt ? ` em ${fmtDateTime(p.signedAt)}` : ''}</div>
                  )}
                  {/* Linha 4: ações */}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-700/60">
                    <div className="flex gap-1.5 flex-wrap items-center">
                      <button onClick={(e) => openLinkModal(p, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg transition-colors text-xs font-medium">🔗 Link</button>
                      {canEdit && acoes.gerarComIa && (
                        <button onClick={(e) => { e.stopPropagation(); setGenerationTarget(p); }}
                          title={p.currentDocumentId ? 'Proposta gerada pelo HUB: ver versões ou gerar de novo' : 'Escrever a proposta com a IA a partir do briefing do lead e do orçamento'}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium ${p.currentDocumentId ? 'bg-central-elevado border border-central-borda text-central-texto font-semibold hover:bg-central-elevado' : 'bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado'}`}>✨ {p.currentDocumentId ? 'Gerada ✓' : 'Gerar com IA'}</button>
                      )}
                      {acoes.enviar && <button onClick={(e) => openSendEmailModal(p, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg transition-colors text-xs font-medium">📧 Enviar</button>}
                      {canEdit && <button onClick={(e) => handleDuplicateProposal(p, e)} data-duplicar-proposta className="flex items-center gap-1.5 px-3 py-1.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg transition-colors text-xs font-medium" title={isExpired ? 'Cria uma cópia em Rascunho para renovar a validade' : 'Cria uma cópia em Rascunho, com os mesmos itens'}>📋 Duplicar</button>}
                      {!isExpired && <button
                        onClick={(e) => handleCustomPdfClick(p, e)}
                        title={(p.customPdfS3Key || p.customHtmlS3Key)
                          ? 'Proposta usa documento externo — clique para substituir ou remover'
                          : 'Anexar PDF ou HTML externo (ex.: proposta desenhada por IA) — vira o documento do link público'}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium ${(p.customPdfS3Key || p.customHtmlS3Key) ? 'bg-central-elevado border border-central-borda text-central-texto font-semibold hover:bg-central-elevado' : 'bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado'}`}
                      >📎 {(p.customPdfS3Key || p.customHtmlS3Key) ? 'Documento ✓' : 'PDF/HTML'}</button>}
                      {(p.acceptedByName || p.acceptedAt || p.rejectedByName || p.rejectionReason) && (
                        <button onClick={(e) => { e.stopPropagation(); setAcceptModalProposal(p); }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium ${p.status === 'Recusada' ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`}>👤 {p.status === 'Recusada' ? 'Detalhes da Recusa' : 'Detalhes do Aceite'}</button>
                      )}
                      {/* Gerar Contrato liberado em qualquer proposta VIVA (07/07): o
                          contrato nasce Rascunho e não compromete nada — antes exigia
                          marcar 'Aceita' só pra gerar a minuta, sujando o funil. */}
                      {acoes.gerarContrato && (
                        <button onClick={(e) => openContractModal(p, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg transition-colors text-xs font-medium">📄 Gerar Contrato</button>
                      )}
                      {p.signatureStatus === 'assinado' && (
                        <button onClick={(e) => handleOpenSignedProposalPdf(p, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-xs font-medium">🖊 PDF Assinado</button>
                      )}
                      <div onClick={(e) => e.stopPropagation()} data-menu-do-cartao>
                        <MaisAcoes itens={itensDoMenu} ancora="esquerda" abrePara="baixo" title={`Mais ações da proposta ${p.code}`} />
                      </div>
                    </div>
                    {linkedContract && (
                      <span role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); onVerContrato && onVerContrato(); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onVerContrato && onVerContrato(); } }} title="Abrir a aba Contrato" data-vinculo-do-contrato className="text-[11px] text-slate-300 whitespace-nowrap cursor-pointer hover:underline">→ Contrato #{linkedContract.id} <span className={`px-1.5 py-0.5 rounded ${contractStatusColors[linkedContract.status] || 'bg-slate-600'}`}>{linkedContract.status}</span></span>
                    )}
                  </div>
                </div>
              );
            })}
            {deadProposals.length > 0 && (
              <button type="button" onClick={() => setMostrarEncerradas((v) => !v)} aria-expanded={mostrarEncerradas} data-propostas-encerradas
                className="w-full text-left px-3 py-2.5 rounded-lg border border-dashed border-central-borda text-sm text-central-texto-fraco hover:text-central-texto">
                {mostrarEncerradas ? '▾' : '▸'} {fraseDasEncerradas} <span className="text-slate-500">· fora da soma</span>
              </button>
            )}
          </div>
        </EstadoDaLista>
      </div>
      </>}

      {/* CONTRATOS DIGITAIS: a aba Contrato da ficha */}
      {secao === 'contrato' && (
        <EstadoDaLista
          carga={carga}
          temDados={temFonte && digitalContracts.length > 0}
          oQue="os contratos"
          onTentarDeNovo={loadData}
          vazio={(
            <div className="text-center text-slate-500 py-8 bg-slate-800/50 rounded-xl border border-slate-700" data-sem-contrato>
              <p>Nenhum contrato gerado.</p>
              <p className="text-sm mt-1">O contrato nasce da proposta aceita: Propostas → Gerar Contrato.</p>
            </div>
          )}
        >
          <div className="space-y-3">
            {digitalContracts.map((c: any) => {
              // Mesma regra do cartão da proposta: barra só na exceção, no tom do selo (aguardando = warning; cancelado = danger).
              const cAccent = c.status === 'Aguardando Assinatura' ? TONE_BARRA.warning : c.status === 'Cancelado' ? TONE_BARRA.danger : SEM_BARRA;
              const signers: any[] = Array.isArray(c.signers) ? c.signers : [];
              const signedN = signers.filter((s) => s.status === 'signed').length;
              return (
              <div key={c.id} data-cartao-do-contrato className={`bg-central-cartao rounded-lg border border-central-borda border-l-[3px] ${cAccent} p-3`}>
                {/* Linha 1: tipo + id + proposta + status + valores */}
                <div className="flex justify-between items-center gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span title={c.documentKind === 'aditivo' ? 'Termo aditivo' : 'Contrato digital'}>✍️</span>
                    <span className="font-mono text-xs text-slate-300 bg-slate-700 px-2 py-0.5 rounded">#{c.id}</span>
                    {/* Aditivo não tem proposta nem lead: dizer "da null" seria
                        pior que não dizer nada. Ele se identifica pelo cliente e
                        pelo contrato a que se prende. */}
                    {c.documentKind === 'aditivo' ? (
                      <>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-300 whitespace-nowrap">aditivo</span>
                        <span className="text-xs text-slate-400 truncate">{c.clientName || `contrato ${c.contractId}`}</span>
                      </>
                    ) : (
                      c.proposalCode
                      ? <span role="link" tabIndex={0} onClick={() => onVerProposta && onVerProposta()} onKeyDown={(e) => { if (e.key === 'Enter') onVerProposta && onVerProposta(); }} title="Abrir a aba Propostas" className="text-xs text-slate-300 truncate cursor-pointer hover:underline">da {c.proposalCode}</span>
                      : <span className="text-xs text-slate-400 truncate">{c.clientName || ''}</span>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${contractStatusColors[c.status] || 'bg-slate-600'}`}>{c.status}</span>
                  </div>
                  <div className="text-right whitespace-nowrap text-xs">
                    <span className="text-slate-500">MRR</span> <span className="font-semibold text-green-400">R$ {fmt(c.totalMRR)}</span>
                    <span className="text-slate-600 mx-1">·</span>
                    <span className="text-slate-500">P&S</span> <span className={`font-semibold ${TONE_TEXT.ps}`}>R$ {fmt(c.totalPS)}</span>
                  </div>
                </div>
                {/* Linha 2: datas */}
                <div className="text-[11px] text-slate-500 mt-1.5">Gerado {fmtDate(c.createdAt)}{c.signedAt && ` • Assinado ${fmtDate(c.signedAt)}`}</div>
                {/* Linha 3: signatários */}
                {signers.length > 0 && (
                  <div className="text-[11px] mt-1 flex items-center gap-1.5 flex-wrap">
                    <span className="text-slate-500">Assinantes ({signedN}/{signers.length}):</span>
                    {signers.map((s, i) => (
                      <span key={i} className={s.status === 'signed' ? 'text-green-400' : 'text-yellow-400'} title={s.email || ''}>
                        {s.name || s.email}{s.status === 'signed' ? ` ✓${s.signedAt ? ` (${fmtDate(s.signedAt)})` : ''}` : ' ⏳'}{i < signers.length - 1 ? ' ·' : ''}
                      </span>
                    ))}
                  </div>
                )}
                {/* Linha 4: ações */}
                <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-700/60">
                  <div className="flex gap-1.5 flex-wrap items-center">
                    {c.status === 'Assinado' ? (
                      <button onClick={() => handleViewSignedDoc(c.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-xs font-medium">📄 Ver Documento Assinado</button>
                    ) : (
                      <button onClick={() => handleViewContractPdf(c.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg transition-colors text-xs font-medium">Visualizar</button>
                    )}
                    {c.status === 'Rascunho' && (
                      <button onClick={(e) => openReviewEmailModal(c, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg transition-colors text-xs font-medium" title="Envia a minuta (PDF) para o cliente revisar antes da assinatura">📧 Enviar p/ validação</button>
                    )}
                    {c.status === 'Rascunho' && (
                      <button onClick={(e) => openSignatureModal(c, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-xs font-medium">✉️ Enviar p/ Assinatura</button>
                    )}
                    {signers.length > 0 && (
                      <button onClick={(e) => handleViewSignersStatus(c, e)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-xs font-medium">👥 Signatários</button>
                    )}
                    {(c.status === 'Aguardando Assinatura' || (canEdit && c.status !== 'Assinado')) && (
                      <div onClick={(e) => e.stopPropagation()} data-menu-do-cartao>
                        <MaisAcoes
                          ancora="esquerda"
                          title={`Mais ações do contrato ${c.id}`}
                          itens={[
                            ...(c.status === 'Aguardando Assinatura' ? [{ label: 'Reenviar lembrete', onClick: () => handleResendNotification(c, SEM_EVENTO) }] : []),
                            ...(c.status === 'Aguardando Assinatura' ? [{ label: 'Cancelar assinatura', onClick: () => handleCancelSignature(c, SEM_EVENTO) }] : []),
                            ...(canEdit && c.status !== 'Assinado' ? [{ label: 'Excluir contrato', danger: true, separadoAntes: true, onClick: () => handleDeleteContract(c, SEM_EVENTO) }] : []),
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </EstadoDaLista>
      )}

      {/* MODAL LINK PÚBLICO */}
      {linkModalOpen && selectedProposalForLink && (
        // Template escolhido: não fecha ao clicar fora (como já era). O gradiente azul
        // vira o PanelHeader; os botões de copiar/abrir são neutros (não gravam).
        <CascaDeModal
          largura="xs"
          titulo="Link Público"
          subtitulo={selectedProposalForLink.code}
          onClose={() => setLinkModalOpen(false)}
          corpoClassName="p-6 space-y-4"
          rodape={
            <>
              <button onClick={() => { const link = getPublicLink(); const sep = link.includes('?') ? '&' : '?'; window.open(link + sep + 'preview=true', '_blank'); }} className={`flex-1 px-4 py-2 rounded-lg text-sm ${acaoPrincipal}`}>👁️ Pré-visualizar (sem marcar)</button>
              <button onClick={() => setLinkModalOpen(false)} className={`px-4 py-2 rounded-lg text-sm ${acaoSecundaria}`}>Fechar</button>
            </>
          }
        >
              {(() => {
                // L1 (28/10/2026): o Rascunho é dito no topo, e a escolha do Copiar fica à vista. Depois de marcar, a
                // faixa verde (ou vermelha, se o servidor recusou) substitui o aviso.
                if (marcacao?.ok) {
                  return <div className={`rounded-lg border px-3 py-2 text-sm ${TONE_BORDER.success} ${TONE_SURFACE.success}`} data-marcacao="ok"><span className={`font-medium ${TONE_TEXT.success}`}>Link copiado e proposta marcada como Enviada às {marcacao.hora}.</span> O cliente já vê Aceitar, e a abertura passa a ser registrada.</div>;
                }
                const m = marcarComoEnviada(selectedProposalForLink);
                if (m.motivo === 'nao_e_rascunho') return null;
                return (
                  <div className="space-y-3" data-link-em-rascunho={m.pode ? 'pode-marcar' : 'travada'}>
                    <div className={`rounded-lg border px-3 py-2 text-sm ${TONE_BORDER.warning} ${TONE_SURFACE.warning}`}>
                      {m.pode
                        ? <><span className={`font-medium ${TONE_TEXT.warning}`}>Esta proposta está em Rascunho.</span> Quem abrir o link não vê Aceitar nem Recusar, e a abertura não é registrada.</>
                        : <span className={TONE_TEXT.warning} data-trava-do-gate>{m.frase}</span>}
                    </div>
                    {marcacao && !marcacao.ok && (
                      <div role="alert" className={`rounded-lg border px-3 py-2 text-sm ${TONE_BORDER.danger} ${TONE_SURFACE.danger}`} data-marcacao="falhou">
                        <span className={TONE_TEXT.danger}>O link foi copiado, mas a proposta continua em Rascunho: {marcacao.motivo}</span>
                      </div>
                    )}
                    {m.pode && (
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Ao copiar</p>
                        <div className="inline-flex rounded-lg border border-central-borda overflow-hidden text-xs" role="radiogroup" aria-label="Ao copiar">
                          {([['marcar', 'Copiar e marcar como enviada'], ['conferir', 'Copiar só para conferir']] as const).map(([v, rotulo]) => (
                            <button key={v} type="button" role="radio" aria-checked={aoCopiar === v} onClick={() => setAoCopiar(v)} data-ao-copiar={v}
                              className={`px-3 py-1.5 ${aoCopiar === v ? 'bg-central-elevado text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}>{rotulo}</button>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Marcar como enviada libera o Aceitar no link e passa a registrar a abertura. "Só para conferir" deixa em Rascunho.</p>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Template da Proposta</label>
                <select value={selectedTemplateId || ''} onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}>
                  <option value="">Sem template</option>
                  {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name} {t.isDefault ? '(Padrão)' : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Link para enviar ao cliente</label>
                <p className="text-xs text-slate-500 mb-2" data-texto-da-abertura>📧 Com a proposta enviada, a abertura pelo cliente fica registrada: ela muda para <span className={`font-medium ${TONE_TEXT.info}`}>Visualizada</span> e entra no histórico do lead. Em Rascunho, não registra. É o mesmo link que vai no botão "Enviar" por e-mail.</p>
                <div className="flex gap-2">
                  <input type="text" value={getPublicLink()} readOnly className={`flex-1 px-3 py-2 ${campo} rounded-lg text-sm`} />
                  <button onClick={copyLink} data-copiar-link className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${linkCopied ? TONE_BADGE.open : 'bg-slate-700 hover:bg-slate-600 text-white'}`}>{linkCopied ? '✓ Copiado!' : rotuloDoCopiar}</button>
                </div>
              </div>
              <div className="border-t border-central-borda pt-3">
                <label className="block text-sm font-medium text-slate-200 mb-1">🟢 Botão do template WhatsApp</label>
                <p className="text-xs text-slate-500 mb-2">Cole este valor na <span className="text-slate-300 font-medium">variável do botão "Ver proposta"</span> ao enviar o template no sistema de atendimento. É só a parte depois de <code className="text-slate-300">/p/</code> (a base <code className="text-slate-400">hub.ipcom.app.br/p/</code> já está no template). <span className="text-slate-400">Não cole a URL inteira.</span></p>
                <div className="flex gap-2">
                  <input type="text" value={getWhatsAppButtonValue()} readOnly className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${campo}`} />
                  <button onClick={copyWhatsAppButton} data-copiar-whatsapp className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${waBtnCopied ? TONE_BADGE.open : 'bg-slate-700 hover:bg-slate-600 text-white'}`}>{waBtnCopied ? '✓ Copiado!' : rotuloDoCopiar}</button>
                </div>
              </div>
              <div className="border-t border-central-borda pt-3">
                <p className="text-xs text-slate-500">👁️ Conferência interna — "Pré-visualizar" abre uma prévia que <span className="text-slate-300 font-medium">não</span> marca como visualizada (não afeta o monitoramento do cliente).</p>
              </div>
        </CascaDeModal>
      )}

      {/* MODAL ENVIAR PROPOSTA POR EMAIL */}
      {sendEmailModalOpen && (selectedProposalForEmail || selectedContractForReview) && (() => {
        const isReview = emailModalMode === 'contract-review';
        const ent = isReview ? selectedContractForReview : selectedProposalForEmail;
        return (
        // Destinatários digitados: não fecha ao clicar fora (como já era). Envio para
        // fora: só a moldura muda, o handler é o mesmo. Uma primária laranja nos dois
        // modos (o ciano da minuta saiu: ciano não é acento do HUB).
        <CascaDeModal
          largura="xs"
          titulo={isReview ? 'Enviar minuta para validação' : 'Enviar Proposta por Email'}
          subtitulo={isReview ? `Contrato #${ent.id} · ${ent.proposalCode || ''}` : ent.code}
          onClose={() => setSendEmailModalOpen(false)}
          corpoClassName="p-6 space-y-4"
          rodape={!sendEmailResult?.sentTo.length ? (
            <>
              <button
                onClick={handleSendProposalEmail}
                disabled={sendingProposalEmail || emailRecipients.length === 0}
                className={`flex-1 px-4 py-2 rounded-lg text-sm disabled:opacity-50 ${acaoPrincipal}`}
              >
                {sendingProposalEmail ? '⏳ Enviando...' : `📧 Enviar para ${emailRecipients.length} email${emailRecipients.length !== 1 ? 's' : ''}`}
              </button>
              <button onClick={() => setSendEmailModalOpen(false)} className={`px-4 py-2 rounded-lg text-sm ${acaoSecundaria}`}>Cancelar</button>
            </>
          ) : (
            <button onClick={() => setSendEmailModalOpen(false)} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium text-sm">Fechar</button>
          )}
        >
              {isReview && (
                <div className={`border rounded-lg p-3 text-xs ${TONE_SURFACE.info} ${TONE_BORDER.info} ${TONE_TEXT.info}`}>
                  📄 O cliente recebe o <strong>PDF da minuta</strong> para revisar. Não vai para assinatura — é só validação.
                </div>
              )}
              <div className="bg-central-elevado rounded-lg p-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><label className="text-xs text-slate-500">Cliente</label><p className="font-semibold text-white">{ent.clientTradeName || ent.clientName}</p></div>
                  <div>
                    <label className="text-xs text-slate-500">Valores</label>
                    {/* MRR (recorrente) e setup (único) NÃO se somam — são naturezas diferentes */}
                    <p className={`font-semibold ${TONE_TEXT.open}`}>
                      {Number(ent.totalMRR) > 0 && <>R$ {fmt(ent.totalMRR)}/mês</>}
                      {Number(ent.totalMRR) > 0 && Number(ent.totalPS) > 0 && <span className="text-slate-400"> + </span>}
                      {Number(ent.totalPS) > 0 && <span className={TONE_TEXT.ps}>R$ {fmt(ent.totalPS)} setup</span>}
                      {!(Number(ent.totalMRR) > 0) && !(Number(ent.totalPS) > 0) && <>R$ 0,00</>}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Destinatários ({emailRecipients.length})</label>
                <div className="space-y-2 mb-3">
                  {emailRecipients.map(email => (
                    <div key={email} className="flex items-center justify-between bg-central-elevado px-3 py-2 rounded-lg border border-central-borda">
                      <span className="text-sm text-white">{email}</span>
                      <button onClick={() => handleRemoveEmailRecipient(email)} className="text-red-400 hover:text-red-300 text-lg px-2">&times;</button>
                    </div>
                  ))}
                  {emailRecipients.length === 0 && <p className="text-sm text-slate-500 text-center py-2">Nenhum destinatário adicionado</p>}
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newEmailInput}
                    onChange={e => setNewEmailInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddEmailRecipient()}
                    placeholder="Digite um email..."
                    className={`flex-1 px-3 py-2 ${campo} rounded-lg text-sm`}
                  />
                  <button onClick={handleAddEmailRecipient} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm font-medium">Adicionar</button>
                </div>
              </div>

              {sendEmailResult && (
                <div className={`p-3 rounded-lg text-sm border ${sendEmailResult.errors.length > 0 && sendEmailResult.sentTo.length === 0 ? `${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}` : `${TONE_SURFACE.open} ${TONE_BORDER.open} ${TONE_TEXT.open}`}`}>
                  {sendEmailResult.sentTo.length > 0 && <p>Enviado para: {sendEmailResult.sentTo.join(', ')}</p>}
                  {sendEmailResult.errors.length > 0 && <p>Erro ao enviar para: {sendEmailResult.errors.join(', ')}</p>}
                </div>
              )}
        </CascaDeModal>
        );
      })()}

      {/* MODAL GERAR CONTRATO */}
      {contractModalOpen && selectedProposalForContract && (
        // Template escolhido: não fecha ao clicar fora (como já era). Cliente e CNPJ na
        // linha mono, P&S e MRR na faixa (tons do §12.4); a prévia rola por dentro.
        <CascaDeModal
          largura="xl"
          titulo="Gerar Contrato Digital"
          subtitulo={`Proposta ${selectedProposalForContract.code} · ${selectedProposalForContract.clientName} · ${selectedProposalForContract.clientCnpj ? maskCNPJ(selectedProposalForContract.clientCnpj) : '-'}`}
          onClose={() => setContractModalOpen(false)}
          faixa={[
            { k: 'Valor P&S', v: `R$ ${fmt(selectedProposalForContract.totalPS)}`, cls: TONE_TEXT.ps },
            { k: 'Valor MRR', v: `R$ ${fmt(selectedProposalForContract.totalMRR)}`, cls: TONE_TEXT.open },
          ]}
          corpoClassName="p-6 flex flex-col"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setContractModalOpen(false)} className={`px-6 py-2 rounded-lg ${acaoSecundaria}`}>Cancelar</button>
              <button onClick={handleGenerateContract} disabled={generatingContract || !selectedContractTemplateId} className={`px-6 py-2 rounded-lg disabled:opacity-50 ${acaoPrincipal}`}>{generatingContract ? '⏳ Gerando...' : '📄 Gerar Contrato'}</button>
            </div>
          }
        >
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">Template do Contrato</label>
                <select value={selectedContractTemplateId || ''} onChange={(e) => setSelectedContractTemplateId(e.target.value ? Number(e.target.value) : null)} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}>
                  <option value="">Selecione um template</option>
                  {contractTemplates.map((t: any) => <option key={t.id} value={t.id}>{t.name} {t.isDefault ? '(Padrão)' : ''}</option>)}
                </select>
                {erroDosModelos
                  ? <p className="text-sm text-amber-300 mt-2" data-modelos="nao-carregou">Não sei se há templates de contrato: a lista não carregou ({erroDosModelos}).</p>
                  : contractTemplates.length === 0 && <p className="text-sm text-red-400 mt-2">⚠️ Nenhum template cadastrado. Vá em Templates → Templates de Contrato.</p>}
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <label className="block text-sm font-medium text-slate-300 mb-2">Preview do Contrato</label>
                <div className="flex-1 min-h-[16rem] overflow-y-auto border border-central-borda rounded-lg p-4 bg-central-elevado">
                  {selectedContractTemplateId ? <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans leading-relaxed">{contractPreview || 'Carregando...'}</pre>
                    : <div className="text-center text-slate-500 py-12"><p className="text-4xl mb-2">📄</p><p>Selecione um template</p></div>}
                </div>
              </div>
        </CascaDeModal>
      )}

      {/* MODAL ASSINATURA DIGITAL (contrato digital OU PDF timbrado da proposta) */}
      {signatureModalOpen && (selectedContractForSignature || signatureProposalTarget) && (
        // Signatários digitados: não fecha ao clicar fora (como já era). Envio para fora
        // (ClickSign): só a moldura muda, o handler é o mesmo. O roxo vira a primária.
        <CascaDeModal
          largura="base"
          titulo="Enviar para Assinatura"
          subtitulo={`${signatureProposalTarget ? `PDF timbrado da proposta ${signatureProposalTarget.code}` : `Contrato #${selectedContractForSignature.id} · ${selectedContractForSignature.proposalCode || ''}`} · ${(signatureProposalTarget || selectedContractForSignature).clientName}`}
          onClose={closeSignatureModal}
          faixa={[
            { k: 'Valor MRR', v: `R$ ${fmt((signatureProposalTarget || selectedContractForSignature).totalMRR)}`, cls: TONE_TEXT.open },
            { k: 'Valor P&S', v: `R$ ${fmt((signatureProposalTarget || selectedContractForSignature).totalPS)}`, cls: TONE_TEXT.ps },
          ]}
          corpoClassName="p-6 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button
                onClick={closeSignatureModal}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-700 font-medium text-sm"
              >
                Cancelar
              </button>
              {/* Fica primary DE PROPÓSITO (Fase 2, tela 2): é o mesmo verbo do App e da SignatureBatchModal, que ainda não estão na casca, e o crm.cores.test exige a mesma cor em todo lugar. Vira fantasma quando eles virarem. */}
              <button
                onClick={handleSendForSignature}
                disabled={sendingForSignature || signers.some(s => !s.email || !s.name)}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium text-sm disabled:opacity-50"
              >
                {sendingForSignature ? '⏳ Enviando...' : `✉️ Enviar para ${signers.length} signatário${signers.length > 1 ? 's' : ''}`}
              </button>
            </div>
          }
        >

              {signatureProposalTarget && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Template do PDF (papel timbrado)</label>
                  <select value={selectedTemplateId || ''} onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}>
                    <option value="">Template padrão</option>
                    {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name} {t.isDefault ? '(Padrão)' : ''}</option>)}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">O PDF é gerado agora com este template e enviado ao ClickSign — é este documento que será assinado.</p>
                </div>
              )}

              {/* Mesmo editor do termo aditivo: um só, porque duas cópias
                  divergiriam no dia em que a ClickSign mudasse uma exigência. */}
              <EditorDeSignatarios signers={signers} onChange={setSigners} />

              <div className={`border rounded-lg p-3 text-sm ${TONE_SURFACE.info} ${TONE_BORDER.info} ${TONE_TEXT.info}`}>
                <p><strong>Como funciona:</strong></p>
                <ul className="list-disc list-inside mt-1 text-xs text-slate-300 space-y-1">
                  <li>Cada signatário receberá um email com seu link individual</li>
                  <li>A assinatura é feita digitalmente via ClickSign</li>
                  <li>Você será notificado quando todos assinarem</li>
                </ul>
              </div>
        </CascaDeModal>
      )}

      {/* MODAL STATUS DOS SIGNATÁRIOS — só leitura: passa a fechar ao clicar fora. */}
      {signersStatusModalOpen && (
        <CascaDeModal
          largura="sm"
          fecharAoClicarFora
          titulo="Status das Assinaturas"
          subtitulo={signersStatusData?.proposalCode ? `Contrato #${signersStatusData.contractId} • ${signersStatusData.proposalCode}` : undefined}
          onClose={() => setSignersStatusModalOpen(false)}
          corpoClassName="p-6"
          rodape={<button onClick={() => setSignersStatusModalOpen(false)} className="ml-auto px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium text-sm">Fechar</button>}
        >
              {loadingSignersStatus ? (
                <div className="text-center py-8 text-slate-400">Carregando status dos signatários...</div>
              ) : signersStatusData?.error ? (
                <div className="text-center py-8 text-red-400">{signersStatusData.error}</div>
              ) : signersStatusData?.signers?.length > 0 ? (
                <div className="space-y-4">
                  {/* Progresso */}
                  <div className="bg-central-elevado rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-400">Progresso</span>
                      <span className="text-sm font-semibold text-white">{signersStatusData.signedCount}/{signersStatusData.totalSigners} assinaram</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-3">
                      <div
                        className="bg-green-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${signersStatusData.completionPercentage}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {signersStatusData.pendingCount > 0
                        ? `Aguardando ${signersStatusData.pendingCount} assinatura(s)`
                        : 'Todas as assinaturas concluídas!'}
                    </p>
                  </div>

                  {/* Lista de signatários */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-slate-400 uppercase">Signatários</h4>
                    {signersStatusData.signers.map((signer: any, idx: number) => (
                      <div key={idx} className={`p-3 rounded-lg border ${signer.status === 'signed' ? `${TONE_SURFACE.open} ${TONE_BORDER.open}` : 'bg-slate-800 border-slate-700'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-white">{signer.name}</p>
                            <p className="text-xs text-slate-400">{signer.email}</p>
                            {signer.cpf && <p className="text-xs text-slate-500">CPF: {signer.cpf}</p>}
                          </div>
                          <div className="text-right">
                            {signer.status === 'signed' ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${TONE_BADGE.open}`}>
                                ✓ Assinou
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${TONE_BADGE.warning}`}>
                                ⏳ Pendente
                              </span>
                            )}
                            <p className="text-xs text-slate-500 mt-1 capitalize">{signer.role === 'witness' ? 'Testemunha' : signer.role === 'approve' ? 'Aprovador' : 'Assinante'}</p>
                          </div>
                        </div>
                        {signer.signedAt && (
                          <p className={`text-xs mt-2 ${TONE_TEXT.open}`}>Assinado em {new Date(signer.signedAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p>
                        )}
                        {/* O link DESTE signatário (15/10/2026, pedido do Heitor): para mandar por WhatsApp quando o e-mail do
                            ClickSign não chega. Só pendente com a chave própria; sem ela, nada (nunca o link de outro). */}
                        {signer.status !== 'signed' && <CopiarLinkDeAssinatura signer={signer} className='mt-2' />}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  {signersStatusData?.message || 'Nenhum signatário encontrado'}
                </div>
              )}
        </CascaDeModal>
      )}

      {/* MODAL DETALHES DO ACEITE / RECUSA DA PROPOSTA */}
      {acceptModalProposal && (() => {
        const p = acceptModalProposal;
        const recusada = p.status === 'Recusada' || (!p.acceptedByName && !p.acceptedAt && (p.rejectedByName || p.rejectionReason));
        const row = (label: string, value: any) => value ? (
          <div className="flex justify-between gap-4 py-1.5 border-b border-slate-800 last:border-0">
            <span className="text-slate-500 text-sm whitespace-nowrap">{label}</span>
            <span className="text-slate-200 text-sm text-right break-all">{value}</span>
          </div>
        ) : null;
        return (
          // Só leitura: fecha ao clicar fora, como já era. O gradiente verde/vermelho
          // vira selo do mapa (open/danger) ao lado do título.
          <CascaDeModal
            largura="xs"
            fecharAoClicarFora
            titulo={recusada ? 'Detalhes da Recusa' : 'Detalhes do Aceite'}
            subtitulo={p.code}
            selos={<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${recusada ? TONE_BADGE.danger : TONE_BADGE.open}`}>{recusada ? 'Recusada' : 'Aceita'}</span>}
            onClose={() => setAcceptModalProposal(null)}
            corpoClassName="p-5"
            rodape={<button onClick={() => setAcceptModalProposal(null)} className="ml-auto px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium text-sm">Fechar</button>}
          >
                {recusada ? (
                  <div className="space-y-0.5">
                    {row('Recusado por', p.rejectedByName)}
                    {row('Email', p.rejectedByEmail)}
                    {row('Motivo', p.rejectionReason)}
                    {row('Observações', p.rejectionNotes)}
                    {row('Recusado em', p.rejectedAt ? fmtDateTime(p.rejectedAt) : null)}
                    {row('Visualizado em', p.viewedAt ? fmtDateTime(p.viewedAt) : null)}
                    {row('IP', p.rejectedByIp)}
                    {!p.rejectedByName && !p.rejectionReason && <p className="text-slate-400 text-sm text-center py-4">Sem detalhes da recusa registrados.</p>}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {row('Aceito por', p.acceptedByName)}
                    {row('Email', p.acceptedByEmail)}
                    {row('CPF', p.acceptedByCpf)}
                    {row('Cargo/Função', p.acceptedByRole)}
                    {row('Aceito em', p.acceptedAt ? fmtDateTime(p.acceptedAt) : null)}
                    {row('Visualizado em', p.viewedAt ? fmtDateTime(p.viewedAt) : null)}
                    {row('IP de origem', p.acceptedByIp)}
                    {row('Navegador', p.acceptedByUserAgent)}
                    {!p.acceptedByName && !p.acceptedAt && <p className="text-slate-400 text-sm text-center py-4">Sem detalhes do aceite registrados (ex.: aprovação manual).</p>}
                  </div>
                )}
          </CascaDeModal>
        );
      })()}

      {/* MODAL CRIAR ASSINATURA */}
      {subscriptionModalOpen && selectedProposalForSubscription && (
        // Cliente escolhido: não fecha ao clicar fora (como já era). "Dados da Proposta"
        // vira linha mono (empresa · CNPJ) e faixa (MRR, P&S, vigência, parcelas).
        <CascaDeModal
          largura="base"
          titulo="Criar Assinatura"
          subtitulo={`Proposta ${selectedProposalForSubscription.code} · ${selectedProposalForSubscription.clientName} · ${selectedProposalForSubscription.clientCnpj || '-'}`}
          onClose={() => setSubscriptionModalOpen(false)}
          faixa={[
            { k: 'Valor MRR', v: `R$ ${fmt(selectedProposalForSubscription.totalMRR)}`, cls: TONE_TEXT.open },
            { k: 'Valor P&S', v: `R$ ${fmt(selectedProposalForSubscription.totalPS)}`, cls: TONE_TEXT.ps },
            { k: 'Vigência', v: `${selectedProposalForSubscription.contractMonths || 12} meses` },
            { k: 'Parcelas P&S', v: `${selectedProposalForSubscription.installments || 1}x` },
          ]}
          corpoClassName="p-6 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button
                onClick={() => setSubscriptionModalOpen(false)}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-700 font-medium text-sm"
              >
                Cancelar
              </button>
              {/* Fica primary DE PROPÓSITO (Fase 2, tela 2): é o mesmo verbo do App e da SignatureBatchModal, que ainda não estão na casca, e o crm.cores.test exige a mesma cor em todo lugar. Vira fantasma quando eles virarem. */}
              <button
                onClick={handleCreateSubscription}
                disabled={creatingSubscription || !selectedClientId}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium text-sm disabled:opacity-50"
              >
                {creatingSubscription ? '⏳ Criando...' : '📋 Criar Assinatura'}
              </button>
            </div>
          }
        >

              {/* Seleção de cliente */}
              <div className="bg-central-elevado rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Vincular a Cliente Existente</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Buscar por nome, razão social ou CNPJ..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-sm placeholder:text-slate-500 ${campo}`}
                  />
                  <div className="max-h-48 overflow-y-auto border border-central-borda rounded-lg">
                    {filteredClients.length === 0 ? (
                      <p className="text-center text-slate-500 py-4 text-sm">Nenhum cliente encontrado</p>
                    ) : (
                      filteredClients.slice(0, 50).map((c: any) => (
                        <div
                          key={c.id}
                          onClick={() => setSelectedClientId(c.id)}
                          className={`p-3 cursor-pointer border-b border-slate-700 last:border-b-0 hover:bg-slate-700/50 transition-colors ${selectedClientId === c.id ? 'bg-slate-700/60 border-l-2 border-l-orange-500' : ''}`}
                        >
                          <p className="font-medium text-white">{c.name || c.companyName}</p>
                          <p className="text-xs text-slate-400">{c.companyName !== c.name ? c.companyName : ''} {c.cnpj ? `• ${maskCNPJ(c.cnpj)}` : ''}</p>
                        </div>
                      ))
                    )}
                  </div>
                  {selectedClientId && (
                    <div className="bg-slate-700/40 border border-slate-600 rounded-lg p-3">
                      <p className="text-sm text-slate-200">
                        <strong>Cliente selecionado:</strong> {clients.find((c: any) => c.id === selectedClientId)?.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Info sobre contrato digital */}
              <div className={`border rounded-lg p-3 text-sm ${TONE_SURFACE.info} ${TONE_BORDER.info} ${TONE_TEXT.info}`}>
                <p><strong>📄 Contrato Digital:</strong></p>
                <p className="text-xs text-slate-300 mt-1">
                  Se houver um contrato digital assinado vinculado a esta proposta, ele será anexado automaticamente à assinatura.
                </p>
              </div>
        </CascaDeModal>
      )}

      {/* MODAL EDIÇÃO/CRIAÇÃO */}
      {aceiteManual && (() => {
        const recusa = porqueNaoAprovar(aceiteManual.nome, aceiteManual.canal);
        return (
        <CascaDeModal
          largura="sm"
          titulo="Registrar o aceite"
          subtitulo={aceiteManual.proposal.code}
          onClose={() => !aceiteManual.salvando && setAceiteManual(null)}
          corpoClassName="p-5 space-y-3"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setAceiteManual(null)} disabled={aceiteManual.salvando} className={`px-4 py-2 rounded-lg ${acaoSecundaria}`}>Cancelar</button>
              <button onClick={registrarAceite} disabled={!!recusa || aceiteManual.salvando} title={recusa || undefined} className={`px-4 py-2 rounded-lg disabled:opacity-50 ${acaoPrincipal}`}>{aceiteManual.salvando ? 'Registrando…' : 'Registrar aceite'}</button>
            </div>
          }
        >
          <p className="text-sm text-slate-300">O cliente aceitou fora do link. Fica "Aceita" com o nome e o canal abaixo, e o seu usuário no histórico; sem CPF nem IP, que só o aceite pelo link registra. Ela sai do MRR potencial e libera a criação da assinatura.</p>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Quem aceitou *</label>
            <input type="text" value={aceiteManual.nome} onChange={(e) => setAceiteManual({ ...aceiteManual, nome: e.target.value })} placeholder="Nome de quem aceitou, no cliente" className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Canal *</label>
            <select value={aceiteManual.canal} onChange={(e) => setAceiteManual({ ...aceiteManual, canal: e.target.value })} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`}>
              <option value="">Escolha…</option>
              {CANAIS_DO_ACEITE.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </CascaDeModal>
        );
      })()}

      {modalOpen && (
        // Editor: não fecha ao clicar fora, como já era. O gradiente laranja sai do
        // cabeçalho (§10.4): o título é a proposta, o modo vira selo, e a empresa vai
        // para a linha mono. O laranja fica só no "Salvar Proposta".
        <CascaDeModal
          largura="lg"
          titulo={editingProposal ? editingProposal.code : 'Nova Proposta'}
          subtitulo={[clientTradeName || clientName, clientCnpj ? maskCNPJ(clientCnpj) : null].filter(Boolean).join(' · ') || undefined}
          selos={<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${editingProposal?.status === 'Aceita' ? TONE_BADGE.success : TONE_BADGE.neutral}`}>{editingProposal?.status === 'Aceita' ? 'Aceita · travada' : editingProposal ? 'Editando' : 'Nova'}</span>}
          onClose={() => setModalOpen(false)}
          // C6 (12/10/2026): a proposta vencida avisa ao abrir, e o aviso some quando a validade nova passa de hoje.
          aviso={avisoDaPropostaVencida(editingProposal, validUntil) ? (
            <p role="alert" className={`mx-5 mt-3 px-3 py-2 rounded border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-testid="aviso-da-proposta-vencida">
              {avisoDaPropostaVencida(editingProposal, validUntil)} A validade fica em Condições.
            </p>
          ) : undefined}
          // Os totais na FAIXA da casca (14/10/2026, "conforme o novo padrão" do Heitor): ficavam num retângulo preto
          // (bg-central-fundo) no pé de Itens; agora sob o cabeçalho, à vista com qualquer seção aberta.
          faixa={[
            { k: 'Total P&S', v: `R$ ${fmt(totals.totalPS)}` },
            { k: 'Total MRR', v: `R$ ${fmt(totals.totalMRR)}` },
            {
              k: 'Investimento',
              // mensal e único NÃO se somam: naturezas diferentes.
              v: (
                <span data-investimento>
                  {investimento.mensal > 0 && <span>R$ {fmt(investimento.mensal)}/mês</span>}
                  {investimento.mensal > 0 && investimento.unico > 0 && <span className="text-central-texto-fraco"> + </span>}
                  {investimento.unico > 0 && <span>R$ {fmt(investimento.unico)} único</span>}
                  {investimento.mensal <= 0 && investimento.unico <= 0 && <span className="text-central-texto-fraco">R$ 0,00</span>}
                </span>
              ),
            },
          ]}
          corpoClassName="p-6 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setModalOpen(false)} className={`px-6 py-2 rounded-lg ${acaoSecundaria}`}>{editingProposal?.status === 'Aceita' ? 'Fechar' : 'Cancelar'}</button>
              {editingProposal?.status === 'Aceita' ? (
                <button onClick={(e) => { setModalOpen(false); handleDuplicateProposal(editingProposal, e); }} className={`px-6 py-2 rounded-lg ${acaoPrincipal}`}>Duplicar como nova versão</button>
              ) : (
              <button onClick={handleSave} disabled={saving || !!porqueSalvarTravado} title={porqueSalvarTravado} className={`px-6 py-2 rounded-lg disabled:opacity-50 ${acaoPrincipal}`}>{saving ? 'Salvando...' : 'Salvar Proposta'}</button>
              )}
            </div>
          }
        >
              {/* E5 (12/10/2026): aceita é travada. O update do backend não olhava status e o link público mostrava os
                  números novos sob o selo "Aceita"; agora o editor só lê, e o caminho é a nova versão. */}
              {editingProposal?.status === 'Aceita' && (
                <div role="status" className={`border rounded-lg p-3 text-sm ${TONE_SURFACE.success} ${TONE_BORDER.success} ${TONE_TEXT.success}`}>
                  {fraseDaPropostaTravada(editingProposal)}
                </div>
              )}
              <fieldset disabled={editingProposal?.status === 'Aceita'} className="space-y-4 min-w-0">
              {/* Seções recolhíveis: seis blocos numa rolagem só faziam o vendedor
                  passar por cima de tudo para mudar uma quantidade. Cada cabeçalho
                  carrega uma linha de resumo — fechar esconde a EDIÇÃO, nunca o
                  estado. As linhas vêm de funções com teste em resumoDaProposta. */}
              <CollapsibleSection
                icon={<Icon name="predio" className="w-4 h-4 text-central-texto-fraco" />}
                title="Dados da empresa"
                variante="cartao"
                summary={resumoDaEmpresa({ clientTradeName, clientName, clientCnpj })}
                /* Proposta nova de lead sem razão social abre aqui: seria o único
                   campo obrigatório escondido atrás de um clique. */
                defaultOpen={editingProposal?.status === 'Aceita' || !String(clientName || '').trim() && !String(clientTradeName || '').trim()}
              >
                {/* E6 (12/10/2026): com lead, o backend sobrescreve estes campos com os do lead a cada edição; editar aqui
                    não gravava e não avisava. Só leitura, com o caminho. */}
                {leadId ? <p className="text-xs text-slate-400 mb-2" data-testid="dados-do-lead">{FRASE_DOS_DADOS_DO_LEAD}</p> : null}
                <div className="grid grid-cols-3 gap-4">
                  <div><label className="block text-xs text-slate-400 mb-1">Razão Social</label><input type="text" readOnly={!!leadId} value={clientName} onChange={e => setClientName(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} /></div>
                  <div><label className="block text-xs text-slate-400 mb-1">Nome Fantasia</label><input type="text" readOnly={!!leadId} value={clientTradeName} onChange={e => setClientTradeName(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} /></div>
                  <div><label className="block text-xs text-slate-400 mb-1">CNPJ</label><input type="text" readOnly={!!leadId} value={maskCNPJ(clientCnpj || '')} onChange={e => setClientCnpj(e.target.value.replace(/\D/g, ''))} maxLength={18} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} /></div>
                </div>
              </CollapsibleSection>
              <CollapsibleSection
                icon={<Icon name="pacote" className="w-4 h-4 text-central-texto-fraco" />}
                title="Itens"
                variante="cartao"
                summary={resumoDosItens(proposalItems.length, investimento)}
                defaultOpen
              >
                {/* C3 (12/10/2026): o "+ Adicionar" saiu; um jeito só de adicionar item, a caixa "Buscar produto…" abaixo. */}
                {/* Tabela compacta: 1 linha por item; clique expande pra trocar
                    produto / editar nome / ver descrição */}
                <div className="rounded-lg border border-central-borda overflow-hidden">
                  <div className="flex items-center gap-2 px-2 py-1.5 border-b border-central-borda text-[11px] uppercase text-central-texto-fraco font-semibold">
                    {/* alça de arraste + expandir: as duas colunas sem rótulo */}
                    <span className="w-4"></span>
                    <span className="w-5"></span>
                    <span className="flex-1">Produto</span>
                    <span className="w-14 text-center">Qtd</span>
                    <span className="w-24 text-center">Vlr Unit</span>
                    <span className="w-14 text-center">Desc%</span>
                    <span className="w-28 text-right pr-1">Subtotal</span>
                    <span className="w-8"></span>
                  </div>
                  {proposalItems.map((item, idx) => {
                    const selectedProduct = products.find((p: any) => p.id === item.productId);
                    const isExpanded = expandedItemId === item.id;
                    const isPS = item.type === 'Serviço' || item.type === 'PS';
                    // Mesmo critério do documento (ver `linhaInclusa`): linha de
                    // valor zero é o que já vem dentro do pacote, não erro de
                    // digitação. Na PRO20260087 são três entre oito itens, e sem
                    // marcação elas pareciam engano de quem montou.
                    const incluso = linhaInclusa(item);
                    // Derivada de pacote: quantidade, preço e produto são conta,
                    // não escolha. O TEXTO continua do vendedor — é ele que
                    // distingue "incluso no pacote" da linha avulsa do mesmo
                    // código, lado a lado, no documento do cliente.
                    const derivada = ehLinhaDerivada(item);
                    const pacoteDaLinha = derivada
                      ? products.find((p: any) => p.id === item.includedByProductId)
                      : null;
                    const porqueTravada = derivada
                      ? `Vem de ${pacoteDaLinha?.code || 'um pacote'}: quantidade e valor são calculados, e a linha sai junto se o pacote sair. O nome e a descrição você edita expandindo.`
                      : '';
                    return (
                      <React.Fragment key={item.id}>
                        <div
                          onDragOver={(e) => { if (arrastando != null) { e.preventDefault(); e.currentTarget.classList.add('ring-1', 'ring-orange-500'); } }}
                          onDragLeave={(e) => e.currentTarget.classList.remove('ring-1', 'ring-orange-500')}
                          onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('ring-1', 'ring-orange-500'); soltarEm(idx); }}
                          className={`flex items-center gap-2 px-2 py-1.5 border-t border-slate-700/60 ${isExpanded ? 'bg-slate-800' : 'hover:bg-slate-800/60'} ${arrastando === idx ? 'opacity-40' : ''}`}
                        >
                          {/* A alça arrasta, não a linha inteira: a linha tem três
                              campos de valor, e arrastar por cima deles tiraria o
                              foco de quem está digitando. Seta para cima e para
                              baixo fazem o mesmo com o teclado. */}
                          {derivada ? (
                            <span className="w-4 shrink-0 text-center text-sky-500/70 select-none" title={porqueTravada}>↳</span>
                          ) : (
                          <span
                            draggable
                            tabIndex={0}
                            role="button"
                            aria-label={`Mover item ${idx + 1} de ${proposalItems.length}`}
                            onDragStart={(e) => { setArrastando(idx); e.dataTransfer.effectAllowed = 'move'; }}
                            onDragEnd={() => setArrastando(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp') { e.preventDefault(); moverPorTeclado(idx, -1); }
                              if (e.key === 'ArrowDown') { e.preventDefault(); moverPorTeclado(idx, 1); }
                            }}
                            className="w-4 shrink-0 text-center text-slate-600 hover:text-slate-300 cursor-grab active:cursor-grabbing select-none focus:outline-none focus:text-orange-400"
                            title="Arraste para reordenar — ou use ↑ ↓ com a alça selecionada. A ordem aqui é a ordem impressa na proposta."
                          >
                            ⠿
                          </span>
                          )}
                          <button onClick={() => setExpandedItemId(isExpanded ? null : item.id)} className="w-5 text-slate-400 hover:text-white text-sm" title={isExpanded ? 'Recolher' : 'Expandir (trocar produto / descrição)'}>
                            {isExpanded ? '▾' : '▸'}
                          </button>
                          <div className="flex-1 min-w-0 cursor-pointer flex items-center gap-2" onClick={() => setExpandedItemId(isExpanded ? null : item.id)}>
                            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${isPS ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>{isPS ? 'PS' : 'MRR'}</span>
                            {incluso && (
                              <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-300" title="Já vem no pacote: entra na proposta sem somar valor, e o cliente lê &quot;Incluso&quot;">
                                Incluso
                              </span>
                            )}
                            {cobraAcimaDoContratado(item) && (
                              <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300"
                                title={`A faixa vale para todas as unidades, e cobrar como ${quantidadeFaturada(item)} sai mais barato que cobrar ${item.quantity}. O subtotal ao lado já é esse — nao e quantidade x unitario.`}>
                                faturado como {quantidadeFaturada(item)}
                              </span>
                            )}
                            <span className="truncate text-sm text-white" title={item.name || selectedProduct?.name || ''}>
                              {item.name || selectedProduct?.name || <span className="text-slate-500 italic">clique para escolher o produto…</span>}
                            </span>
                          </div>
                          <input type="number" min="1" value={item.quantity || 1} onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))} disabled={derivada} title={porqueTravada} className={`w-14 px-1.5 py-1 rounded text-sm text-center disabled:bg-slate-800 disabled:text-slate-400 disabled:cursor-not-allowed ${campo}`} />
                          <CampoDeUnitario valor={Number(item.unitPrice) || 0} onValor={(n) => updateItem(item.id, 'unitPrice', n)} disabled={derivada} title={porqueTravada} className={`w-24 px-1.5 py-1 rounded text-sm text-right disabled:bg-slate-800 disabled:text-slate-400 disabled:cursor-not-allowed ${campo}`} />
                          <input type="number" min="0" max="100" step="0.1" value={item.discount || 0} onChange={e => updateItem(item.id, 'discount', Number(e.target.value))} disabled={derivada} title={porqueTravada} className={`w-14 px-1.5 py-1 rounded text-sm text-center disabled:bg-slate-800 disabled:text-slate-400 disabled:cursor-not-allowed ${campo}`} />
                          {/* "Incluso" no lugar de R$ 0,00, a mesma palavra que o
                              cliente lê na composição — o vendedor confere na tela o
                              que vai sair no documento. */}
                          {incluso ? (
                            <div className="w-28 text-right pr-1 text-sm text-sky-300">Incluso</div>
                          ) : (
                            <div className="w-28 text-right pr-1 text-sm font-semibold text-white">R$ {fmt(item.subtotal || 0)}</div>
                          )}
                          <button onClick={() => removeItem(item.id)} disabled={derivada} className="w-8 text-slate-500 hover:text-red-400 disabled:text-slate-700 disabled:cursor-not-allowed text-sm" title={derivada ? porqueTravada : 'Remover item'}>🗑</button>
                        </div>
                        {faixaPendente[item.id] && (
                          <div className={`mx-9 mb-2 px-3 py-1.5 rounded border text-xs flex items-center gap-2 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-faixa-pendente={item.id}>
                            {faixaPendente[item.id] === COTANDO ? (
                              <span>Cotando o preço da faixa…</span>
                            ) : (
                              <>
                                <span>⚠ Preço da faixa não confirmado ({faixaPendente[item.id]}): o unitário na linha é o de referência do produto, não o da faixa.</span>
                                <button type="button" onClick={() => applyGuidePrice(item.id, Number(item.productId), Number(item.quantity) || 1)} className="ml-auto underline font-medium">Recotar</button>
                              </>
                            )}
                          </div>
                        )}
                        {isExpanded && (
                          <div className="px-9 py-3 bg-central-fundo/60 border-t border-central-borda/40 space-y-2">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="relative">
                                <label className="block text-[11px] text-slate-500 mb-1">Produto</label>
                                <input
                                  type="text"
                                  value={productSearchOpen === String(item.id) ? productSearch : (selectedProduct ? `${selectedProduct.code} - ${selectedProduct.name}` : '')}
                                  onChange={e => { setProductSearch(e.target.value); setProductSearchOpen(String(item.id)); }}
                                  onFocus={() => { setProductSearch(''); setProductSearchOpen(String(item.id)); }}
                                  onBlur={() => setTimeout(() => setProductSearchOpen(null), 200)}
                                  placeholder="Buscar produto..."
                                  className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                                />
                                {productSearchOpen === String(item.id) && (
                                  <div className="absolute z-50 w-full mt-1 bg-central-elevado border border-slate-600 rounded-lg max-h-48 overflow-y-auto shadow-xl">
                                    {products.filter((p: any) => p.status && (`${p.code} ${p.name}`.toLowerCase().includes(productSearch.toLowerCase()))).map((p: any) => (
                                      <div key={p.id} onMouseDown={() => { updateItem(item.id, 'productId', p.id); setProductSearchOpen(null); setProductSearch(''); }} className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-700 ${p.id === item.productId ? 'bg-slate-700 text-orange-400' : 'text-white'}`}>
                                        {p.code} - {p.name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-[11px] text-slate-500 mb-1">Nome do item (sai na proposta/NF)</label>
                                <input type="text" value={item.name || ''} onChange={e => updateItem(item.id, 'name', e.target.value)} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} />
                              </div>
                            </div>
                            {selectedProduct?.description && (
                              <p className="text-xs whitespace-pre-line leading-relaxed text-slate-400">{selectedProduct.description}</p>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {proposalItems.length === 0 && (
                    <div className="px-3 py-4 text-sm text-slate-500 border-t border-central-borda/60">Nenhum item — adicione abaixo.</div>
                  )}
                </div>

                <div className="mt-3">
                  {/* Adicionar novo produto */}
                  <div className="rounded-lg border border-central-borda p-3" data-adicionar-produto>
                    <p className="text-central-texto-fraco text-sm mb-2 flex items-center gap-1.5"><Icon name="plus" className="w-3.5 h-3.5" /> Adicionar produto</p>
                    {produtosNaoCarregaram && (
                      <p role="alert" className={`mb-3 px-3 py-2 rounded border text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-produtos="nao-carregaram">
                        Os produtos não carregaram ({cargaDosProdutos?.erro}): não dá para escolher produto agora. Os itens já na proposta seguem com o nome que têm.
                      </p>
                    )}
                    <div className="relative">
                      <input
                        type="text"
                        value={productSearchOpen === 'new' ? productSearch : ''}
                        onChange={e => { setProductSearch(e.target.value); setProductSearchOpen('new'); }}
                        onFocus={() => { setProductSearch(''); setProductSearchOpen('new'); }}
                        onBlur={() => setTimeout(() => setProductSearchOpen(null), 200)}
                        placeholder="Buscar produto..."
                        className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`}
                      />
                      {productSearchOpen === 'new' && (
                        <div className="absolute z-50 w-full mt-1 bg-central-elevado border border-slate-600 rounded-lg max-h-48 overflow-y-auto shadow-xl">
                          {products.filter((p: any) => (p.status === true || p.status === 'Ativo') && (`${p.code} ${p.name}`.toLowerCase().includes(productSearch.toLowerCase()))).map((p: any) => (
                            <div key={p.id} onMouseDown={() => {
                              const unitPrice = Number(p.value) || 0;
                              const novoId = Date.now();
                              setProposalItems([...proposalItems, {
                                id: novoId,
                                productId: p.id,
                                productCode: p.code || '',
                                name: p.name || '',
                                description: p.description || '',
                                type: p.type || 'MRR',
                                quantity: 1,
                                unitPrice: unitPrice,
                                discount: 0,
                                subtotal: unitPrice,
                              }]);
                              // ⛔ Este caminho nunca cotava: o item de produto com faixa entrava com o preço de REFERÊNCIA
                              // e, salvo sem mexer na quantidade, ia assim para a proposta (08/10/2026). Agora cota como
                              // a troca de produto, e a linha fica "cotando…" (Salvar travado) até a resposta.
                              applyGuidePrice(novoId, p.id, 1);
                              setProductSearchOpen(null);
                              setProductSearch('');
                            }} className="px-3 py-2 text-sm text-white cursor-pointer hover:bg-slate-700">
                              {p.code} - {p.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desconto geral: era um campo grande no meio dos totais. Agora no pé dos Itens, onde ele age; os totais estão
                    na faixa da casca. */}
                <div className="mt-4 flex items-center justify-end gap-2 text-sm">
                  <label htmlFor="desconto-geral" className="text-central-texto-fraco">Desconto geral</label>
                  <input id="desconto-geral" type="number" value={discountPercent} onChange={e => setDiscountPercent(Number(e.target.value))} className={`w-20 px-2 py-1 ${campo} rounded-lg text-right`} min="0" max="100" />
                  <span className="text-central-texto-fraco">%</span>
                </div>
              </CollapsibleSection>
              <CollapsibleSection
                defaultOpen={editingProposal?.status === 'Aceita'}
                icon={<Icon name="ajustes" className="w-4 h-4 text-central-texto-fraco" />}
                title="Condições"
                variante="cartao"
                summary={resumoDasCondicoes({ validUntil, installments, paymentMethod, contractMonths })}
              >
                <div className="grid grid-cols-4 gap-4">
                  <div><label className="block text-xs text-slate-400 mb-1">Validade</label><input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} /></div>
                  <div><label className="block text-xs text-slate-400 mb-1">Parcelas P&S</label><input type="number" value={installments} onChange={e => setInstallments(Number(e.target.value))} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} min="1" /></div>
                  <div><label className="block text-xs text-slate-400 mb-1">Pagamento</label><select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`}><option>Boleto</option><option>PIX</option><option>Cartão</option><option>Transferência</option></select></div>
                  <div><label className="block text-xs text-slate-400 mb-1">Vigência (meses)</label><input type="number" value={contractMonths} onChange={e => setContractMonths(Number(e.target.value))} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} min="1" /></div>
                </div>
              </CollapsibleSection>
              <CollapsibleSection
                defaultOpen={editingProposal?.status === 'Aceita'}
                icon={<Icon name="fileText" className="w-4 h-4 text-central-texto-fraco" />}
                title="Documento do cliente"
                variante="cartao"
                summary="A proposta pública muda de texto conforme a escolha."
                badge={{ text: templateVariant === 'whatsapp' ? 'WhatsApp' : 'Telefonia', tone: 'neutro' }}
              >
                <p className="text-xs text-slate-400 mb-3">
                  Muda o texto da proposta pública — a capa e a página "o que está incluso".
                  Valores, composição e desconto por volume são os mesmos nas duas.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { v: 'voz', t: 'Telefonia', d: 'Central em nuvem, ramal no navegador e no celular, fila e gravação.' },
                    { v: 'whatsapp', t: 'WhatsApp', d: 'Número oficial verificado, conversa com dono, fila, histórico e Agente de IA.' },
                  ] as const).map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setTemplateVariant(o.v)}
                      aria-pressed={templateVariant === o.v}
                      className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${templateVariant === o.v ? 'bg-orange-500/15 border-orange-500/60' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}
                    >
                      <span className={`block text-sm font-medium ${templateVariant === o.v ? 'text-orange-300' : 'text-slate-200'}`}>{o.t}</span>
                      <span className="block text-xs text-slate-400 mt-0.5">{o.d}</span>
                    </button>
                  ))}
                </div>
              </CollapsibleSection>
              <CollapsibleSection
                defaultOpen={editingProposal?.status === 'Aceita'}
                icon={<Icon name="edit" className="w-4 h-4 text-central-texto-fraco" />}
                title="Observações"
                variante="cartao"
                summary={resumoDasObservacoes(notes)}
              >
                <textarea value={notes} onChange={e => setNotes(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} rows={3} placeholder="Observações visíveis na proposta..." />
              </CollapsibleSection>
              </fieldset>
        </CascaDeModal>
      )}
    </div>
  );
};

export default ProposalsTab;
